<template>
	<div class="FactoryStockManage">
		<header class="fsm-header">
			<div>
				<h2 class="fsm-title">工厂库存</h2>
				<p class="fsm-sub">成品/原料仅为标签；发货按工厂清单加工尺寸、切料清单与 SKU 配方扣减，允许负库存</p>
			</div>
		</header>

		<el-tabs v-model="tab" class="fsm-tabs" @tab-click="onTab">
			<el-tab-pane label="物料库存" name="materials" />
			<el-tab-pane label="未发货原料需求" name="demand" />
			<el-tab-pane label="SKU 配方" name="bom" />
			<el-tab-pane label="流水" name="ledger" />
		</el-tabs>

		<!-- 物料库存：左树（工厂分类）+ 右列（物料列表），左树可隐藏 -->
		<section v-show="tab === 'materials'" class="fsm-card">
			<div v-if="!activeRootId" class="fsm-empty">请先在上方选择产品大类</div>
			<template v-else>
			<div class="fsm-mat-layout" :class="{ 'is-tree-hidden': !matCatTreeVisible }">
				<aside v-show="matCatTreeVisible" class="fsm-mat-layout__tree">
					<div class="fsm-mat-layout__tree-head">
						<span
							class="fsm-mat-layout__tree-title"
							role="button"
							tabindex="0"
							title="点击查看全部物料"
							@click="selectMatCategory('all')"
							@keydown.enter.prevent="selectMatCategory('all')">
							工厂分类
						</span>
						<button
							type="button"
							class="fsm-mat-layout__tree-hide"
							title="隐藏分类树"
							@click="setMatCatTreeVisible(false)">
							<i class="el-icon-d-arrow-left" />
						</button>
					</div>
					<div class="fsm-mat-layout__tree-toolbar">
						<button type="button" class="fsm-mat-tb-btn" title="刷新" @click="loadMatCategories(true)">
							<i class="el-icon-refresh" />
						</button>
						<button type="button" class="fsm-mat-tb-btn is-success" title="添加分类" @click="openMatCatAdd">
							<i class="el-icon-plus" />
						</button>
						<button
							type="button"
							class="fsm-mat-tb-btn is-primary"
							title="编辑分类"
							:disabled="!selectedMatCategory"
							@click="openMatCatEdit()">
							<i class="el-icon-edit" />
						</button>
						<button
							type="button"
							class="fsm-mat-tb-btn is-danger"
							title="删除分类"
							:disabled="!selectedMatCategory"
							@click="delMatCategory(selectedMatCategory)">
							<i class="el-icon-delete" />
						</button>
						<button type="button" class="fsm-mat-tb-btn" title="分类列表管理" @click="openMatCatManage">
							<i class="el-icon-setting" />
						</button>
					</div>
					<nav class="fsm-mat-cat-tree">
						<button
							type="button"
							class="fsm-mat-cat-tree__item"
							:class="{ 'is-active': matCategoryTab === 'all' }"
							@click="selectMatCategory('all')">
							<span class="fsm-mat-cat-tree__name">全部</span>
						</button>
						<button
							type="button"
							class="fsm-mat-cat-tree__item"
							:class="{ 'is-active': matCategoryTab === 'none' }"
							@click="selectMatCategory('none')">
							<span class="fsm-mat-cat-tree__name">未分类</span>
						</button>
						<div v-if="matCategories.length" class="fsm-mat-cat-tree__group-label">自定义分类</div>
						<button
							v-for="c in matCategories"
							:key="'mc-' + c.id"
							type="button"
							class="fsm-mat-cat-tree__item is-child"
							:class="{ 'is-active': matCategoryTab === String(c.id) }"
							:title="c.name"
							@click="selectMatCategory(String(c.id))">
							<span class="fsm-mat-cat-tree__dot" aria-hidden="true" />
							<span class="fsm-mat-cat-tree__name">{{ c.name }}</span>
						</button>
						<div v-if="!matCategories.length" class="fsm-mat-cat-tree__empty">
							暂无自定义分类
							<button type="button" class="fsm-mat-cat-tree__link" @click="openMatCatAdd">去添加</button>
						</div>
					</nav>
				</aside>

				<main class="fsm-mat-layout__main">
					<div class="fsm-toolbar">
						<div class="fsm-toolbar__filters">
							<el-button
								v-if="!matCatTreeVisible"
								size="mini"
								icon="el-icon-d-arrow-right"
								@click="setMatCatTreeVisible(true)">
								显示分类
							</el-button>
							<span v-if="!matCatTreeVisible" class="fsm-mat-layout__current">{{ currentMatCategoryLabel }}</span>
							<el-input
								v-model="matSearch"
								clearable
								placeholder="搜索物料 / 商品名"
								class="fsm-search"
								prefix-icon="el-icon-search"
								@keyup.enter.native="reloadMaterials"
								@clear="reloadMaterials" />
							<el-select
								v-model="matStockClass"
								class="fsm-kind"
								placeholder="成品/原料"
								@change="reloadMaterials">
								<el-option label="全部划分" value="" />
								<el-option label="成品" value="finished" />
								<el-option label="原料" value="raw" />
							</el-select>
						</div>
						<div class="fsm-toolbar__actions">
							<el-button type="primary" icon="el-icon-plus" @click="openMatAdd()">添加物料大项</el-button>
							<el-button icon="el-icon-goods" @click="openInbound()">进货/调整</el-button>
							<el-button icon="el-icon-refresh" @click="reloadMaterials">刷新</el-button>
						</div>
					</div>

					<p class="fsm-list-meta">
						<template v-if="matMaterialTotal || matTotal">
							共 <strong>{{ matTotal }}</strong> 款商品 · <strong>{{ matMaterialTotal }}</strong> 个物料产品
						</template>
						<span class="fsm-list-meta__hint">
							列表仅展示完整规格组合；双击左侧「商品大项」配置多规格组合，行内可进货/编辑属性
						</span>
					</p>

					<div v-loading="matLoading" class="fsm-mat-list">
						<div v-if="matFlatTable.rows.length" class="fsm-mat-table-wrap">
							<table class="fsm-mat-table">
								<thead>
									<tr>
										<th class="is-index">#</th>
										<th class="is-goods">商品大项</th>
										<th
											v-for="(dim, di) in matFlatTable.dimensions"
											:key="'dim-' + di"
											class="is-dim">
											{{ dim }}
										</th>
										<th class="is-meta">划分</th>
										<th class="is-meta">编码</th>
										<th class="is-meta">单位</th>
										<th class="is-meta is-num">库存</th>
										<th class="is-meta is-num">原料长</th>
										<th class="is-ops">操作</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="(row, idx) in matFlatTable.rows"
										:key="row.rowKey"
										class="fsm-mat-table__row"
										:class="{
											'is-stripe': idx % 2 === 1,
											'is-empty-goods': row.isEmptyGoods,
										}">
										<td class="is-index">{{ idx + 1 }}</td>
										<td
											v-if="row.showGoods"
											class="is-goods is-goods--clickable"
											:rowspan="row.rowspan"
											title="双击配置规格组合"
											@dblclick.stop="openMatSpecEditor(row.group)">
											<div class="fsm-mat-goods-cell">
												<div class="fsm-mat-goods-cell__name">{{ row.group.goods_name }}</div>
												<div v-if="row.group.goods_model" class="fsm-mat-goods-cell__model">{{ row.group.goods_model }}</div>
												<div v-if="row.group.category_name" class="fsm-mat-goods-cell__cat">{{ row.group.category_name }}</div>
												<div class="fsm-mat-goods-cell__hint">双击配置规格</div>
											</div>
										</td>
										<template v-for="(dim, di) in matFlatTable.dimensions">
											<td
												v-if="row.specShow[di]"
												:key="row.rowKey + '-d-' + di"
												class="is-dim"
												:class="{ 'is-merged': row.specRowspan[di] > 1 }"
												:rowspan="row.specRowspan[di]">
												<span v-if="row.specValues[di]" class="fsm-spec-chip">{{ row.specValues[di] }}</span>
												<span v-else class="fsm-spec-empty">—</span>
											</td>
										</template>
										<template v-if="row.item">
											<td class="is-meta">
												<span
													class="fsm-stock-class-tag"
													:class="'is-' + (row.item.stock_class || 'raw')">
													{{ row.item.stock_class_text || '—' }}
												</span>
											</td>
											<td class="is-meta">{{ row.item.code || '—' }}</td>
											<td class="is-meta">{{ row.item.unit || '—' }}</td>
											<td class="is-meta is-num">
												<span :class="{ 'is-neg': Number(row.item.qty) < 0 }">{{ row.item.qty_text }}</span>
											</td>
											<td class="is-meta is-num">{{ row.item.stock_length || '—' }}</td>
											<td class="is-ops">
												<el-button type="text" @click="openInbound(row.item)">进货</el-button>
												<el-button type="text" @click="openMatEdit(row.item, row.group)">编辑</el-button>
												<el-button type="text" class="danger" @click="onMatRowDelete(row.group, row)">删除</el-button>
											</td>
										</template>
										<template v-else>
											<td class="is-meta is-empty-span" :colspan="5">
												<span class="fsm-mat-empty-row">暂无物料</span>
											</td>
											<td class="is-ops">
												<span class="fsm-spec-empty">—</span>
											</td>
										</template>
									</tr>
								</tbody>
							</table>
						</div>
						<el-empty
							v-else-if="!matLoading"
							description="当前分类暂无物料，可「添加物料大项」"
							:image-size="72" />
					</div>
					<div class="fsm-pager">
						<el-pagination
							layout="total, prev, pager, next"
							:total="matTotal"
							:page-size="matLimit"
							:current-page.sync="matPage"
							@current-change="loadMaterials" />
					</div>
				</main>
			</div>
			</template>
		</section>

		<!-- 未发货原料需求：左订单列表 / 右订单统计 -->
		<section v-show="tab === 'demand'" class="fsm-card">
			<div class="fsm-toolbar">
				<div class="fsm-toolbar__filters">
					<span class="fsm-demand-meta">下单时间</span>
					<el-date-picker
						v-model="demandDateRange"
						type="daterange"
						unlink-panels
						range-separator="至"
						start-placeholder="开始日期"
						end-placeholder="结束日期"
						value-format="timestamp"
						:clearable="false"
						class="fsm-demand-range"
						@change="onDemandRangeChange" />
					<el-button type="primary" :loading="demandOrderLoading" @click="loadDemandOrders">查询订单</el-button>
				</div>
				<div class="fsm-toolbar__actions">
					<el-button
						type="primary"
						plain
						:loading="demandLoading"
						:disabled="!demandSelectedIds.length"
						@click="loadDemand">
						汇总已选物料
					</el-button>
				</div>
			</div>
			<p class="fsm-hint fsm-hint--block">
				左侧点选订单，右侧显示与生产详情页一致的商品统计（长度/支数）；勾选后可「汇总已选物料」做配方换算。
			</p>
			<div v-if="demandOrdersQueried" class="fsm-demand-summary">
				<span>未发货订单 <strong>{{ demandSummary.order_count }}</strong> 张</span>
				<span>已选 <strong>{{ demandSelectedIds.length }}</strong> 张</span>
				<span>商品行 <strong>{{ demandSelectedLineTotal }}</strong> / {{ demandSummary.line_total }}</span>
				<span>件数 <strong>{{ demandSelectedGoodsQty }}</strong> / {{ demandSummary.goods_qty_total }}</span>
				<span>金额 <strong>{{ demandSelectedAmountText }}</strong> / {{ demandSummary.amount_total_text }}</span>
			</div>

			<div v-loading="demandOrderLoading" class="fsm-demand-split">
				<aside class="fsm-demand-split__left">
					<div class="fsm-demand-split__title">订单列表</div>
					<el-table
						ref="demandOrderTable"
						:data="demandOrders"
						stripe
						border
						size="small"
						height="420"
						row-key="id"
						highlight-current-row
						:row-class-name="demandRowClassName"
						@selection-change="onDemandOrderSelect"
						@current-change="onDemandOrderCurrent"
						@row-click="onDemandOrderRowClick">
						<el-table-column type="selection" width="40" reserve-selection />
						<el-table-column prop="order_number" label="订单号" width="108" show-overflow-tooltip />
						<el-table-column prop="customer_name" label="订货方" min-width="80" show-overflow-tooltip>
							<template slot-scope="{ row }">{{ row.customer_name || '—' }}</template>
						</el-table-column>
						<el-table-column prop="contacts" label="联系人" width="68" show-overflow-tooltip>
							<template slot-scope="{ row }">{{ row.contacts || '—' }}</template>
						</el-table-column>
						<el-table-column label="金额" width="72" align="right">
							<template slot-scope="{ row }">{{ row.total }}</template>
						</el-table-column>
						<el-table-column label="下单时间" width="136" show-overflow-tooltip>
							<template slot-scope="{ row }">{{ formatTime(row.createtime) }}</template>
						</el-table-column>
					</el-table>
					<el-empty
						v-if="!demandOrderLoading && demandOrdersQueried && !demandOrders.length"
						description="该时间区间内没有未发货订单"
						:image-size="48" />
				</aside>

				<main v-loading="demandDetailLoading" class="fsm-demand-split__right">
					<template v-if="demandActiveOrder">
						<div class="fsm-demand-split__title">
							<span>订单统计 · {{ demandActiveOrder.order_number }}</span>
							<a
								class="fsm-demand-detail-link"
								href="javascript:;"
								@click.prevent="goFactoryDetail(demandActiveOrder.id)">
								打开生产详情
							</a>
						</div>
						<div class="fsm-demand-stats">
							<div class="fsm-demand-stat">
								<span class="fsm-demand-stat__label">订货方</span>
								<strong>{{ demandActiveOrder.customer_name || '—' }}</strong>
							</div>
							<div class="fsm-demand-stat">
								<span class="fsm-demand-stat__label">联系人</span>
								<strong>{{ demandActiveOrder.contacts || '—' }}</strong>
							</div>
							<div class="fsm-demand-stat">
								<span class="fsm-demand-stat__label">电话</span>
								<strong>{{ demandActiveOrder.phone || '—' }}</strong>
							</div>
							<div class="fsm-demand-stat">
								<span class="fsm-demand-stat__label">金额</span>
								<strong>{{ demandActiveOrder.total }}</strong>
							</div>
							<div class="fsm-demand-stat">
								<span class="fsm-demand-stat__label">下单时间</span>
								<strong>{{ formatTime(demandActiveOrder.createtime) }}</strong>
							</div>
						</div>
						<!-- 与 factoryDetail 页「统计」一致：按 SKU/规格汇总长度与支数，不换算物料 -->
						<div v-if="demandOrderGoodsStats.detailRows.length" class="print-goods-stats">
							<div class="print-goods-stats__head">
								<span class="print-goods-stats__title">统计</span>
								<span class="print-goods-stats__sub">
									产品 <span class="num">{{ demandOrderGoodsStats.productTypeText }}</span> 类
								</span>
							</div>
							<div class="print-goods-stats__list">
								<div
									v-for="g in demandOrderGoodsStats.detailRows"
									:key="g.kind + '-' + g.key"
									class="print-goods-stats__row">
									<div class="print-goods-stats__name">{{ g.label }}</div>
									<div class="print-goods-stats__fields">
										<div class="print-goods-stats__field print-goods-stats__field--length">
											<template v-if="g.kind === 'length'">
												<span class="print-goods-stats__label">总长度</span>
												<span class="num">{{ g.lengthText }}</span>
												<span class="print-goods-stats__unit">mm</span>
											</template>
										</div>
										<div class="print-goods-stats__field print-goods-stats__field--qty">
											<span class="print-goods-stats__label">共</span>
											<span class="num">{{ g.qtyText }}</span>
											<span class="print-goods-stats__unit">{{ g.kind === 'length' ? '支' : '个' }}</span>
										</div>
									</div>
								</div>
							</div>
						</div>
						<el-empty
							v-else-if="!demandDetailLoading && demandDetailQueried"
							description="本单暂无可统计商品"
							:image-size="48" />
					</template>
					<el-empty
						v-else
						description="请在左侧点击订单查看统计"
						:image-size="64" />
				</main>
			</div>

			<div v-if="demandQueried" class="fsm-demand-result fsm-demand-result--merge">
				<div class="fsm-demand-result__head">
					<strong>已选订单汇总物料</strong>
					<span>基于 {{ demandOrderCount }} 张订单（配方换算，与右侧生产统计不同）</span>
				</div>
				<el-table v-loading="demandLoading" :data="demandList" stripe border size="small" max-height="260">
					<el-table-column prop="material_code" label="编码" width="96" show-overflow-tooltip />
					<el-table-column prop="material_name" label="物料" min-width="140" show-overflow-tooltip />
					<el-table-column prop="stock_class_text" label="划分" width="64" />
					<el-table-column prop="need_qty_text" label="需要" width="72" align="right" />
					<el-table-column prop="stock_qty_text" label="库存" width="72" align="right">
						<template slot-scope="{ row }">
							<span :class="{ 'is-neg': Number(row.stock_qty) < 0 }">{{ row.stock_qty_text }}</span>
						</template>
					</el-table-column>
					<el-table-column label="差额" width="72" align="right">
						<template slot-scope="{ row }">
							<span :class="{ 'is-neg': Number(row.gap_qty) < 0 }">{{ row.gap_qty_text }}</span>
						</template>
					</el-table-column>
				</el-table>
			</div>
		</section>

		<!-- SKU 配方：添加加工产品 → 关联物料 -->
		<section v-show="tab === 'bom'" class="fsm-card">
			<div v-if="!activeRootId" class="fsm-empty">请先在上方选择产品大类</div>
			<template v-else>
			<div class="fsm-toolbar">
				<div class="fsm-toolbar__filters">
					<span class="fsm-bom-main-title">加工产品</span>
					<span class="fsm-bom-main-meta">共 {{ bomWorkspace.length }} 项 · 已关联物料 {{ bomConfiguredCount }} 项</span>
				</div>
				<div class="fsm-toolbar__actions">
					<el-button type="primary" icon="el-icon-plus" @click="openBomSkuPick">添加加工产品</el-button>
					<el-button icon="el-icon-refresh" :loading="bomWorkspaceLoading" @click="reloadBomTab">刷新</el-button>
				</div>
			</div>
			<div class="fsm-bom-main" v-loading="bomWorkspaceLoading">
				<div v-if="!bomWorkspace.length" class="fsm-bom-drop-empty">
					暂无加工产品。请点右上角「添加加工产品」，双击商品大项加入。
				</div>
				<div class="fsm-bom-list" v-else>
					<div
						v-for="item in bomWorkspace"
						:key="'bw-' + item.id"
						class="fsm-bom-sku"
						:class="{ 'is-target': Number(bomMatTargetId) === Number(item.id) }">
						<div class="fsm-bom-sku__head">
							<div class="fsm-bom-sku__title">
								<strong>{{ item.display_goods_name || item.goods_name }}</strong>
								<span v-if="item.saving" class="fsm-bom-tag is-pending">保存中</span>
								<span
									v-else-if="bomLinkedLineCount(item)"
									class="fsm-bom-tag is-ok">已关联 {{ bomLinkedLineCount(item) }}</span>
								<span v-else class="fsm-bom-tag is-pending">待关联</span>
							</div>
							<div class="fsm-bom-sku__ops">
								<el-button type="text" @click="openBomMatDialog(item, { mode: 'common' })">+公共</el-button>
								<el-button type="text" class="danger" @click="removeBomWorkspaceItem(item)">移除</el-button>
							</div>
						</div>

						<div class="fsm-bom-section fsm-bom-section--inline">
							<span class="fsm-bom-section__label">公共</span>
							<div v-if="bomCommonLines(item).length" class="fsm-bom-chip-list">
								<span
									v-for="row in bomCommonLines(item)"
									:key="'cm-' + item.id + '-' + row.material_id"
									class="fsm-bom-chip">
									{{ row.material_name || ('#' + row.material_id) }}
									<em>{{ bomConsumeTypeShort(row.consume_type) }}</em>
									<button type="button" class="fsm-bom-chip__del" @click="removeBomLineRow(item, row)">×</button>
								</span>
							</div>
							<span v-else class="fsm-bom-inline-empty">未关联</span>
						</div>

						<div class="fsm-bom-section fsm-bom-section--inline">
							<span class="fsm-bom-section__label">灯条</span>
							<el-switch
								:value="!!(item.strip_link && item.strip_link.associate)"
								@change="(on) => onBomStripLinkChange(item, on)" />
							<template v-if="item.strip_link && item.strip_link.associate">
								<el-select
									v-model="item.strip_link.material_id"
									filterable
									clearable
									placeholder="灯条大项"
									size="mini"
									class="fsm-bom-inline-select"
									@change="onBomStripHeaderPick(item)">
									<el-option
										v-for="h in matHeaderOptions"
										:key="'strip-h-' + h.id"
										:label="h.label || h.name"
										:value="h.id" />
								</el-select>
								<span v-if="item.strip_link.material_id" class="fsm-bom-strip-meta">
									{{ bomConsumeTypeShort(item.strip_link.consume_type) }}
									<span
										v-if="Number(item.strip_link.loss_rate) > 0"
										class="fsm-bom-loss">
										· {{ formatBomLossFactor(item.strip_link.loss_rate) }}
									</span>
								</span>
							</template>
						</div>

						<div
							v-if="(item.sku_combos && item.sku_combos.list && item.sku_combos.list.length) || (item.sku_combos && item.sku_combos.associate)"
							class="fsm-bom-section">
							<div class="fsm-bom-section__title fsm-bom-section__title--row">
								<span>规格</span>
								<el-switch
									v-model="item.sku_combos.associate"
									@change="(on) => onBomSkuComboAssociateChange(item, on)" />
							</div>
							<template v-if="item.sku_combos.associate">
								<div
									v-for="tbl in [bomSkuComboTable(item)]"
									:key="'bom-sku-tbl-' + item.id"
									class="fsm-mat-table-wrap fsm-bom-sku-table-wrap">
									<table
										v-if="tbl.rows.length"
										class="fsm-mat-table fsm-bom-sku-table fsm-bom-sku-table--compact">
										<thead>
											<tr>
												<th
													v-for="(dim, di) in tbl.dimensions"
													:key="'bom-dim-h-' + item.id + '-' + di"
													class="is-dim">
													{{ dim }}
												</th>
												<th
													v-if="item.strip_link && item.strip_link.associate"
													class="is-meta is-w">
													宽度
												</th>
												<th class="is-meta">物料</th>
												<th class="is-ops">操作</th>
											</tr>
										</thead>
										<tbody>
											<tr
												v-for="(row, idx) in tbl.rows"
												:key="row.rowKey"
												class="fsm-mat-table__row"
												:class="{ 'is-stripe': idx % 2 === 1 }">
												<template v-for="(dim, di) in tbl.dimensions">
													<td
														v-if="row.specShow[di]"
														:key="'bom-dim-' + row.rowKey + '-' + di"
														class="is-dim"
														:class="{ 'is-merged': row.specRowspan[di] > 1 }"
														:rowspan="row.specRowspan[di]">
														<span v-if="row.specValues[di]" class="fsm-spec-chip">{{ row.specValues[di] }}</span>
														<span v-else class="fsm-spec-empty">—</span>
													</td>
												</template>
												<td
													v-if="item.strip_link && item.strip_link.associate && row.showStripWidth"
													class="is-meta is-w"
													:class="{ 'is-merged': row.stripWidthRowspan > 1 }"
													:rowspan="row.stripWidthRowspan">
													<el-select
														:value="row.strip_width"
														size="mini"
														clearable
														filterable
														allow-create
														default-first-option
														placeholder="宽"
														style="width:72px"
														@change="(v) => onBomModelStripWidthChange(item, row, v)">
														<el-option
															v-for="w in bomStripWidthOptions"
															:key="'sw-' + w"
															:label="w"
															:value="w" />
													</el-select>
												</td>
												<td class="is-meta">
													<template v-if="row.material_id">
														{{ row.material_name || ('#' + row.material_id) }}
														<span class="fsm-bom-mat__drive">{{ bomConsumeTypeShort(row.consume_type) }}</span>
													</template>
													<span v-else class="fsm-muted">—</span>
												</td>
												<td class="is-ops">
													<el-button
														type="text"
														@click="openBomMatDialog(item, {
															mode: 'sku_combo',
															bind_sku_price_id: row.bind_sku_price_id,
															option_name: row.option_name,
															replace_material_id: row.material_id || 0,
														})">
														{{ row.material_id ? '换' : '关联' }}
													</el-button>
													<el-button
														v-if="row.material_id"
														type="text"
														@click="openBomMatDialog(item, {
															mode: 'sku_combo',
															bind_sku_price_id: row.bind_sku_price_id,
															option_name: row.option_name,
															replace_material_id: 0,
														})">
														+
													</el-button>
													<el-button
														v-if="row.material_id"
														type="text"
														class="danger"
														@click="removeBomSkuComboMaterial(item, row)">
														删
													</el-button>
												</td>
											</tr>
										</tbody>
									</table>
									<div v-else class="fsm-bom-inline-empty">暂无上架规格</div>
								</div>
							</template>
						</div>

						<div
							v-for="pg in bomRecipeParameterGroups(item)"
							:key="'pg-' + item.id + '-' + pg.id"
							class="fsm-bom-section"
							:class="{ 'fsm-bom-section--inline': !pg.associate || isBomParamHeaderGroup(pg) }">
							<template v-if="!pg.associate || isBomParamHeaderGroup(pg)">
								<span class="fsm-bom-section__label">{{ pg.name }}<em v-if="pg.must">*</em></span>
								<el-switch
									:value="!!pg.associate"
									@change="(on) => onBomParamAssociateChange(item, pg, on)" />
								<template v-if="pg.associate && isBomParamHeaderGroup(pg)">
									<el-select
										v-model="pg.header_link.material_id"
										filterable
										clearable
										placeholder="开关大项"
										size="mini"
										class="fsm-bom-inline-select"
										@change="onBomParamHeaderPick(item, pg)">
										<el-option
											v-for="h in matHeaderOptions"
											:key="'param-h-' + pg.id + '-' + h.id"
											:label="h.label || h.name"
											:value="h.id" />
									</el-select>
									<span v-if="pg.header_link.material_id" class="fsm-bom-strip-meta">
										{{ bomConsumeTypeShort(pg.header_link.consume_type) }}
									</span>
								</template>
							</template>
							<template v-else>
								<div class="fsm-bom-section__title fsm-bom-section__title--row">
									<span>{{ pg.name }}<em v-if="pg.must">*</em></span>
									<el-switch
										:value="!!pg.associate"
										@change="(on) => onBomParamAssociateChange(item, pg, on)" />
								</div>
								<el-table
									:data="bomParamOptionRows(item, pg)"
									border
									size="mini"
									max-height="180"
									class="fsm-bom-editor fsm-bom-editor--compact">
									<el-table-column label="选项" min-width="90" show-overflow-tooltip>
										<template slot-scope="{ row }">{{ row.option_name }}</template>
									</el-table-column>
									<el-table-column label="物料" min-width="140" show-overflow-tooltip>
										<template slot-scope="{ row }">
											<span v-if="row.material_id">
												{{ row.material_name || ('#' + row.material_id) }}
												<span class="fsm-bom-mat__drive">{{ bomConsumeTypeShort(row.consume_type) }}</span>
											</span>
											<span v-else class="fsm-muted">—</span>
										</template>
									</el-table-column>
									<el-table-column label="" width="56" align="center">
										<template slot-scope="{ row }">
											<el-button
												type="text"
												@click="openBomMatDialog(item, {
													mode: 'param',
													parameter_pid: pg.id,
													parameter_id: row.option_id,
													option_name: row.option_name,
												})">
												{{ row.material_id ? '换' : '关联' }}
											</el-button>
										</template>
									</el-table-column>
								</el-table>
							</template>
						</div>
					</div>
				</div>
			</div>
			</template>
		</section>

		<!-- 流水 -->
		<section v-show="tab === 'ledger'" class="fsm-card">
			<div class="fsm-toolbar">
				<el-select v-model="ledgerType" clearable placeholder="类型" class="fsm-kind" @change="loadLedger">
					<el-option label="进货入库" value="purchase_in" />
					<el-option label="库存调整" value="adjust" />
					<el-option label="发货扣减" value="ship_out" />
					<el-option label="发货冲正" value="ship_reverse" />
				</el-select>
				<el-button icon="el-icon-refresh" @click="loadLedger">刷新</el-button>
			</div>
			<el-table v-loading="ledgerLoading" :data="ledgerList" stripe border size="small">
				<el-table-column label="时间" width="160">
					<template slot-scope="{ row }">{{ formatTime(row.createtime) }}</template>
				</el-table-column>
				<el-table-column prop="type_text" label="类型" width="100" />
				<el-table-column prop="material_code" label="编码" width="110" />
				<el-table-column prop="material_name" label="物料" min-width="140" />
				<el-table-column label="变动" width="100" align="right">
					<template slot-scope="{ row }">
						<span :class="{ 'is-neg': Number(row.qty_delta) < 0 }">{{ row.qty_delta }}</span>
					</template>
				</el-table-column>
				<el-table-column prop="qty_after" label="结余" width="100" align="right" />
				<el-table-column prop="order_id" label="订单ID" width="90" />
				<el-table-column prop="remark" label="备注" min-width="180" show-overflow-tooltip />
			</el-table>
			<div class="fsm-pager">
				<el-pagination
					layout="total, prev, pager, next"
					:total="ledgerTotal"
					:page-size="ledgerLimit"
					:current-page.sync="ledgerPage"
					@current-change="loadLedger" />
			</div>
		</section>

		<!-- 添加商品大项 / 添加子级规格 / 编辑 -->
		<el-dialog
			:title="matDialogTitle"
			:visible.sync="matDialog"
			width="560px"
			append-to-body
			@closed="resetMatFormDialog">
			<p v-if="matFormMode === 'goods'" class="fsm-hint fsm-hint--block">
				物料大项本身即可库存（编码/单位/库存）；添加规格后，大项变为路径，各规格组合为独立物料行。
			</p>
			<p v-else-if="matFormMode === 'node'" class="fsm-hint fsm-hint--block">
				在「{{ matForm.parent_path || matForm.goods_name || '—' }}」下新增规格值，生成一条完整规格组合；「追加规格」为同级组合，「下级规格」为更深一级。
			</p>
			<el-form label-width="100px" @submit.native.prevent="saveMat">
				<!-- 编辑物料 -->
				<template v-if="matFormMode === 'edit'">
					<el-form-item label="物料分类">
						<el-select v-model="matForm.category_id" clearable placeholder="可选，未选则进未分类" style="width:100%">
							<el-option
								v-for="c in matCategories"
								:key="'mf-edit-' + c.id"
								:label="c.name"
								:value="c.id" />
						</el-select>
					</el-form-item>
					<el-form-item label="名称" required>
						<el-input v-model="matForm.name" />
					</el-form-item>
				</template>

				<!-- 添加物料大项：名称 + 库存属性 -->
				<template v-else-if="matFormMode === 'goods'">
					<el-form-item label="物料分类">
						<el-select v-model="matForm.category_id" clearable placeholder="可选，未选则进未分类" style="width:100%">
							<el-option
								v-for="c in matCategories"
								:key="'mf-' + c.id"
								:label="c.name"
								:value="c.id" />
						</el-select>
					</el-form-item>
					<el-form-item label="物料大项" required>
						<el-input
							v-model="matForm.goods_name"
							clearable
							maxlength="64"
							show-word-limit
							placeholder="填写要录入的物料大项，如 铝型材、灯条"
							@keyup.enter.native="saveMat" />
					</el-form-item>
					<el-form-item label="划分" required>
						<el-radio-group v-model="matForm.stock_class">
							<el-radio label="finished">成品</el-radio>
							<el-radio label="raw">原料</el-radio>
						</el-radio-group>
					</el-form-item>
					<el-form-item label="编码">
						<el-input v-model="matForm.code" clearable maxlength="64" placeholder="选填" />
					</el-form-item>
					<el-form-item label="单位">
						<el-input v-model="matForm.unit" placeholder="根 / 米 / 个" />
					</el-form-item>
					<el-form-item label="原料长(mm)">
						<el-input v-model="matForm.stock_length" clearable placeholder="选填，型材类可填如 3000" />
					</el-form-item>
					<el-form-item label="损耗%">
						<el-input
							v-model="matForm.loss_rate"
							clearable
							placeholder="选填，如灯条 10"
							style="max-width:160px" />
						<div class="fsm-hint">挂入配方后按「驱动×系数×(1+损耗%)」扣减。</div>
					</el-form-item>
				</template>

				<!-- 列表：添加子级规格（任意层级通用） -->
				<template v-else-if="matFormMode === 'node'">
					<el-form-item label="商品大项">
						<div class="fsm-mat-goods-locked">{{ matForm.goods_name || '—' }}</div>
					</el-form-item>
					<el-form-item v-if="matForm.parent_path" label="上级规格">
						<div class="fsm-mat-goods-locked">{{ matForm.parent_path }}</div>
					</el-form-item>
					<el-form-item label="规格值" required>
						<el-input
							v-model="matForm.spec_name"
							clearable
							maxlength="64"
							show-word-limit
							placeholder="如 18mm、黑色、加厚款"
							@keyup.enter.native="saveMat" />
					</el-form-item>
					<el-form-item label="编码">
						<el-input
							v-model="matForm.code"
							clearable
							maxlength="64"
							placeholder="选填，最后一层物料建议填写"
							@keyup.enter.native="saveMat" />
					</el-form-item>
				</template>

				<!-- 属性区：仅编辑规格时展示；新增二级规格继承所属一级/组属性 -->
				<template v-if="matFormMode === 'edit'">
					<el-form-item label="划分" required>
						<el-radio-group v-model="matForm.stock_class">
							<el-radio label="finished">成品</el-radio>
							<el-radio label="raw">原料</el-radio>
						</el-radio-group>
						<div class="fsm-hint">仅为标签，不限制用量类型；实际扣减由 SKU 配方 + 工厂清单/切料清单决定。</div>
					</el-form-item>
					<el-form-item label="编码">
						<el-input v-model="matForm.code" clearable placeholder="选填" />
					</el-form-item>
					<el-form-item label="单位">
						<el-input v-model="matForm.unit" placeholder="根 / 米 / 个" />
					</el-form-item>
					<el-form-item label="原料长(mm)">
						<el-input v-model="matForm.stock_length" clearable placeholder="选填，型材类可填如 3000" />
					</el-form-item>
					<el-form-item label="损耗%">
						<el-input
							v-model="matForm.loss_rate"
							clearable
							placeholder="选填，如灯条 10"
							style="max-width:160px" />
						<div class="fsm-hint">
							实际消耗 = 配方驱动量 × 系数 × (1+损耗%/100)。例：10 → 总长度按 110% 计。
						</div>
					</el-form-item>
					<el-form-item label="备注">
						<el-input
							v-model="matForm.remark"
							type="textarea"
							:rows="2"
							:disabled="matFormEditRemarkLocked" />
					</el-form-item>
				</template>
			</el-form>
			<span slot="footer">
				<el-button @click="matDialog = false">取消</el-button>
				<el-button type="primary" :loading="matSaving" @click="saveMat">保存</el-button>
			</span>
		</el-dialog>

		<!-- 物料大项 · 规格组合（对齐商品管理「规格定价」） -->
		<el-dialog
			:title="'规格组合 · ' + (matSpecEditor.goodsName || '物料大项')"
			:visible.sync="matSpecEditor.visible"
			width="960px"
			top="6vh"
			append-to-body
			custom-class="fsm-mat-spec-dialog"
			@opened="scheduleMatSpecSortables"
			@closed="onMatSpecEditorClosed">
			<div v-loading="matSpecEditor.loading" class="fsm-mat-spec-editor">
				<p class="fsm-hint fsm-hint--block">
					先添加规格维度与规格值，系统按组合生成物料行（与商品管理「规格定价」相同）；
					按住左侧图标可拖动调整规格项 / 规格值顺序。
				</p>
				<div class="fsm-mat-spec-dims">
					<div class="fsm-mat-spec-dims__title">规格项</div>
					<div v-if="!matSpecEditor.dimensions.length" class="fsm-mat-spec-empty">
						尚未添加规格维度（如：宽度、色温）
					</div>
					<div ref="matSpecDimsEl" class="fsm-mat-spec-dims__list">
						<div
							v-for="(dim, di) in matSpecEditor.dimensions"
							:key="dim.temp_id"
							class="fsm-mat-spec-dim"
							:data-dim-temp-id="dim.temp_id">
							<div class="fsm-mat-spec-dim__main">
								<span class="fsm-mat-spec-drag el-icon-rank" title="按住拖动调整规格顺序" />
								<span class="fsm-mat-spec-dim__label">规格名称</span>
								<el-input
									v-model="dim.name"
									size="mini"
									maxlength="12"
									placeholder="如：色温"
									style="width:120px"
									@change="rebuildMatSpecCombos" />
								<el-button
									type="text"
									icon="el-icon-delete"
									class="danger"
									@click="removeMatSpecDim(di)" />
							</div>
							<div class="fsm-mat-spec-dim__values">
								<span class="fsm-mat-spec-dim__label">规格值</span>
								<div
									class="fsm-mat-spec-values-list"
									:data-dim-temp-id="dim.temp_id">
									<div
										v-for="(ch, ci) in dim.children"
										:key="ch.temp_id"
										class="fsm-mat-spec-value">
										<span class="fsm-mat-spec-value-drag el-icon-rank" title="按住拖动调整规格值顺序" />
										<el-tag
											size="small"
											closable
											class="fsm-mat-spec-tag"
											@close="removeMatSpecValue(di, ci)">
											<span @dblclick="editMatSpecValue(di, ci)">{{ ch.name || '未命名' }}</span>
										</el-tag>
									</div>
								</div>
								<el-button type="text" icon="el-icon-plus" @click="addMatSpecValue(di)">添加规格值</el-button>
							</div>
						</div>
					</div>
					<el-button type="primary" plain size="mini" icon="el-icon-plus" @click="addMatSpecDim">添加规格</el-button>
				</div>

				<div v-if="matSpecEditor.combos.length" class="fsm-mat-spec-combos">
					<div class="fsm-mat-spec-dims__title">规格组合表（共 {{ matSpecEditor.combos.length }} 行）</div>
					<div
						v-for="tbl in [matSpecEditorComboTable()]"
						:key="'msc-tbl-' + matSpecEditor.headerId"
						class="fsm-mat-table-wrap fsm-mat-spec-combo-wrap">
						<table class="fsm-mat-table fsm-mat-spec-combo-table">
							<thead>
								<tr>
									<th class="is-index">#</th>
									<th
										v-for="(dimName, di) in tbl.dimensions"
										:key="'msc-h-' + di"
										class="is-dim">
										{{ dimName }}
									</th>
									<th class="is-meta">编码</th>
									<th class="is-meta">划分</th>
									<th class="is-meta">单位</th>
									<th class="is-meta is-num">原料长</th>
									<th class="is-meta is-num">损耗%</th>
									<th class="is-meta is-num">库存</th>
									<th class="is-meta">状态</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(row, idx) in tbl.rows"
									:key="row.rowKey"
									class="fsm-mat-table__row"
									:class="{ 'is-stripe': idx % 2 === 1 }">
									<td class="is-index">{{ idx + 1 }}</td>
									<template v-for="(dimName, di) in tbl.dimensions">
										<td
											v-if="row.specShow[di]"
											:key="'msc-d-' + row.rowKey + '-' + di"
											class="is-dim"
											:class="{ 'is-merged': row.specRowspan[di] > 1 }"
											:rowspan="row.specRowspan[di]">
											<span v-if="row.specValues[di]" class="fsm-spec-chip">{{ row.specValues[di] }}</span>
											<span v-else class="fsm-spec-empty">—</span>
										</td>
									</template>
									<td class="is-meta">
										<el-input v-model="row.combo.code" size="mini" clearable placeholder="选填" />
									</td>
									<td class="is-meta">
										<el-select v-model="row.combo.stock_class" size="mini" style="width:100%">
											<el-option label="成品" value="finished" />
											<el-option label="原料" value="raw" />
										</el-select>
									</td>
									<td class="is-meta">
										<el-input v-model="row.combo.unit" size="mini" />
									</td>
									<td class="is-meta is-num">
										<el-input v-model="row.combo.stock_length" size="mini" clearable placeholder="mm" />
									</td>
									<td class="is-meta is-num">
										<el-input v-model="row.combo.loss_rate" size="mini" clearable />
									</td>
									<td class="is-meta is-num">
										{{ row.combo.qty != null ? row.combo.qty : '—' }}
									</td>
									<td class="is-meta">
										<el-switch
											:value="row.combo.status !== 'down'"
											active-text="上"
											inactive-text="下"
											@change="(on) => { row.combo.status = on ? 'up' : 'down'; }" />
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
				<div v-else class="fsm-mat-spec-empty">添加规格值后将自动生成组合行</div>
			</div>
			<span slot="footer" class="fsm-mat-spec-footer">
				<el-button type="text" class="danger" @click="delMatSpecEditorGroup">删除整项</el-button>
				<span class="fsm-mat-spec-footer__spacer" />
				<el-button @click="matSpecEditor.visible = false">取消</el-button>
				<el-button type="primary" :loading="matSpecEditor.saving" @click="saveMatSpecEditor">保存规格组合</el-button>
			</span>
		</el-dialog>

		<!-- 进货/调整 -->
		<el-dialog
			title="进货 / 调整"
			:visible.sync="inDialog"
			width="480px"
			append-to-body
			@open="loadMatOptions">
			<el-form label-width="96px" class="fsm-form" @submit.native.prevent>
				<el-form-item label="物料" required>
					<el-select v-model="inForm.material_id" filterable clearable placeholder="选择物料" style="width:100%">
						<el-option v-for="m in matOptions" :key="m.id" :label="m.label" :value="m.id" />
					</el-select>
				</el-form-item>
				<el-form-item label="类型">
					<el-radio-group v-model="inForm.type">
						<el-radio label="purchase_in">进货入库</el-radio>
						<el-radio label="adjust">库存调整</el-radio>
					</el-radio-group>
				</el-form-item>
				<el-form-item :label="inForm.type === 'adjust' ? '变动数量' : '入库数量'" required>
					<el-input v-model="inForm.qty" placeholder="进货为正；调整可正可负" />
					<div class="fsm-hint">调整：正数增加、负数减少。允许库存为负（先发后补）。</div>
				</el-form-item>
				<el-form-item label="备注">
					<el-input v-model="inForm.remark" type="textarea" :rows="2" placeholder="选填" />
				</el-form-item>
			</el-form>
			<span slot="footer">
				<el-button @click="inDialog = false">取消</el-button>
				<el-button type="primary" :loading="inSaving" @click="submitInbound">确认提交</el-button>
			</span>
		</el-dialog>

		<!-- 添加 / 编辑工厂物料分类（对齐 back-web 侧栏 FormDialog） -->
		<el-dialog
			:title="matCatForm.id ? '编辑分类' : '添加分类'"
			:visible.sync="matCatFormDialog"
			width="440px"
			append-to-body
			@closed="resetMatCatForm">
			<el-form ref="matCatFormRef" :model="matCatForm" label-width="72px" class="fsm-form" @submit.native.prevent>
				<el-form-item label="名称" required>
					<el-input
						v-model="matCatForm.name"
						clearable
						maxlength="64"
						placeholder="分类名称"
						@keyup.enter.native="saveMatCatForm" />
				</el-form-item>
				<el-form-item label="排序">
					<el-input v-model="matCatForm.sort" placeholder="数字越小越靠前" />
				</el-form-item>
			</el-form>
			<span slot="footer">
				<el-button @click="matCatFormDialog = false">取消</el-button>
				<el-button type="primary" :loading="matCatSaving" @click="saveMatCatForm">保存</el-button>
			</span>
		</el-dialog>

		<!-- 管理工厂物料分类（列表） -->
		<el-dialog title="管理物料分类" :visible.sync="matCatDialog" width="560px" append-to-body>
			<div class="fsm-cat-manage">
				<div class="fsm-cat-manage__add">
					<el-button type="primary" icon="el-icon-plus" @click="openMatCatAdd">添加分类</el-button>
				</div>
				<el-table :data="matCategories" border size="small" max-height="360">
					<el-table-column label="名称" min-width="160" prop="name" show-overflow-tooltip />
					<el-table-column label="排序" width="72" prop="sort" align="center" />
					<el-table-column label="操作" width="200" align="center">
						<template slot-scope="{ row }">
							<el-button type="text" @click="openMatCatEdit(row)">编辑</el-button>
							<el-button type="text" :disabled="!canMoveMatCat(row, -1)" @click="moveMatCategory(row, -1)">上移</el-button>
							<el-button type="text" :disabled="!canMoveMatCat(row, 1)" @click="moveMatCategory(row, 1)">下移</el-button>
							<el-button type="text" class="danger" @click="delMatCategory(row)">删除</el-button>
						</template>
					</el-table-column>
				</el-table>
			</div>
			<span slot="footer">
				<el-button type="primary" @click="matCatDialog = false">完成</el-button>
			</span>
		</el-dialog>

		<!-- 配方：添加加工产品（按商品大项，对齐报价目录弹窗） -->
		<el-dialog
			class="good_dialog"
			custom-class="good_dialog_panel"
			:visible.sync="bomPickVisible"
			:show-close="false"
			:append-to-body="true"
			:close-on-click-modal="true"
			@open="onBomPickOpen">
			<div class="good_dialog_body" v-loading="bomPickLoading || bomPickSaving">
				<el-row type="flex" align="middle" class="list_header">
					<el-row type="flex" align="middle" class="search_box">
						<el-input
							v-model="bomPickSearch"
							class="search_input"
							placeholder="搜索产品名称 / 关键词"
							clearable
							@keyup.enter.native="searchBomGood" />
						<el-row type="flex" align="middle" justify="center" class="search_img">
							<i class="el-icon-search" />
						</el-row>
						<div class="search_btn" title="搜索" @click="searchBomGood" />
					</el-row>
					<el-select
						v-model="bomPickTypeIndex"
						class="list_type"
						placeholder="选择分类"
						@change="changeBomGoodType">
						<el-option
							v-for="(item, index) in bomPickTypes"
							:key="'bpt-' + index"
							:label="item.name"
							:value="index" />
					</el-select>
					<button type="button" class="good_dialog_close" title="关闭" @click="bomPickVisible = false">
						<i class="el-icon-close" />
					</button>
				</el-row>
				<p class="fsm-hint" style="margin:8px 12px 0">双击商品大项后选择加工模板（如灯条加工模板）再加入列表。</p>
				<div class="good_list_wrap">
					<el-table
						ref="bomGoodTable"
						class="good_list"
						highlight-current-row
						:data="bomPickGoodList"
						cell-class-name="good_text"
						header-cell-class-name="good_header"
						@row-dblclick="chooseBomGood"
						@current-change="selectBomGood">
						<el-table-column :resizable="false" align="center" width="110" label="图片">
							<template slot-scope="scope">
								<el-row type="flex" justify="center" align="middle" class="good_img">
									<el-image fit="contain" :src="scope.row.image">
										<div slot="error" class="image-slot">
											<i class="el-icon-picture-outline" />
										</div>
									</el-image>
									<span
										v-if="isBomWorkspaceGoods(scope.row.id)"
										class="fsm-pick-sku__tag"
										style="position:absolute;bottom:4px">已在列表</span>
								</el-row>
							</template>
						</el-table-column>
						<el-table-column
							:resizable="false"
							align="center"
							min-width="40%"
							prop="name"
							label="产品名称"
							show-overflow-tooltip />
						<el-table-column
							:resizable="false"
							header-align="center"
							align="left"
							min-width="48%"
							prop="summary"
							label="概述"
							show-overflow-tooltip />
					</el-table>
				</div>
			</div>
		</el-dialog>

		<!-- 配方：选产品后选择加工模板 -->
		<el-dialog
			title="选择加工模板"
			:visible.sync="bomTplVisible"
			width="520px"
			append-to-body
			:close-on-click-modal="false"
			custom-class="fsm-bom-tpl-dialog">
			<p class="fsm-hint" style="margin:0 0 12px">
				已选产品：
				<strong>{{ (bomTplPendingGood && (bomTplPendingGood.name || bomTplPendingGood.goods_name)) || '—' }}</strong>
				；请选择套用模板。
			</p>
			<div v-loading="bomTplLoading" class="fsm-bom-tpl-list">
				<label
					v-for="tpl in bomTplList"
					:key="'bom-tpl-' + tpl.code"
					class="fsm-bom-tpl-card"
					:class="{
						'is-active': bomTplCode === tpl.code,
						'is-disabled': tpl.code !== 'blank' && !tpl.available,
					}">
					<input
						type="radio"
						name="bom-tpl"
						:value="tpl.code"
						:disabled="tpl.code !== 'blank' && !tpl.available"
						v-model="bomTplCode" />
					<div class="fsm-bom-tpl-card__body">
						<div class="fsm-bom-tpl-card__title">
							{{ tpl.name }}
							<span v-if="tpl.code !== 'blank' && !tpl.available" class="fsm-muted">（源配方未配置）</span>
						</div>
						<p class="fsm-bom-tpl-card__desc">{{ tpl.desc }}</p>
						<p v-if="tpl.source_goods_name && tpl.code !== 'blank'" class="fsm-bom-tpl-card__meta">
							源产品：{{ tpl.source_goods_name }}
							<template v-if="tpl.line_count"> · {{ tpl.line_count }} 行物料</template>
						</p>
					</div>
				</label>
				<el-empty v-if="!bomTplLoading && !bomTplList.length" description="暂无可用模板" :image-size="56" />
			</div>
			<span slot="footer">
				<el-button @click="cancelBomTemplatePick">取消</el-button>
				<el-button type="primary" :loading="bomTplSaving" @click="confirmBomTemplatePick">加入列表</el-button>
			</span>
		</el-dialog>

		<!-- 配方：关联物料（当前物料库存） -->
		<el-dialog
			:title="bomMatDialogTitle"
			:visible.sync="bomMatDialogVisible"
			width="860px"
			top="6vh"
			append-to-body
			custom-class="fsm-bom-mat-dialog"
			@open="onBomMatDialogOpen">
			<div class="fsm-bom-mat-dlg">
				<div class="fsm-bom-mat-dlg__target" v-if="bomMatTargetItem()">
					<span class="fsm-bom-mat-dlg__label">当前加工产品</span>
					<div class="fsm-bom-mat-dlg__product">
						<strong>{{ bomMatTargetItem().display_goods_name || bomMatTargetItem().goods_name }}</strong>
						<span v-if="bomMatTargetItem().parameter_text" class="fsm-bom-sku__param">{{ bomMatTargetItem().parameter_text }}</span>
						<span
							v-if="bomMatTargetCtx && (bomMatTargetCtx.mode === 'param' || bomMatTargetCtx.mode === 'sku_combo' || bomMatTargetCtx.mode === 'sku')"
							class="fsm-bom-sku__param">
							· {{ bomMatTargetCtx.option_name || '选项' }}
						</span>
					</div>
				</div>
				<div class="fsm-cat-bar fsm-cat-bar--compact">
					<div class="fsm-cat-bar__main">
						<span class="fsm-cat-bar__label">工厂分类</span>
						<el-tabs v-model="matCategoryTab" class="fsm-cat-tabs" @tab-click="onMatCategoryTab">
							<el-tab-pane label="全部" name="all" />
							<el-tab-pane label="未分类" name="none" />
							<el-tab-pane
								v-for="c in matCategories"
								:key="'bom-dlg-mc-' + c.id"
								:label="c.name"
								:name="String(c.id)" />
						</el-tabs>
					</div>
				</div>
				<div class="fsm-toolbar fsm-toolbar--compact">
					<el-input
						v-model="bomMatSearch"
						clearable
						placeholder="筛选物料名称 / 编码"
						class="fsm-search"
						prefix-icon="el-icon-search" />
				</div>
				<p class="fsm-hint">
					来自「物料库存」（{{ rootName || '当前大类' }} · {{ currentMatCategoryLabel }}）。点击「加入」关联到当前产品，可连续操作。
				</p>
				<div class="fsm-bom-mat-list fsm-bom-mat-list--dialog">
					<el-table
						v-if="bomMatLibrary.length"
						:data="bomMatLibrary"
						border
						size="mini"
						height="420"
						class="fsm-bom-mat-table"
						:row-class-name="bomMatRowClassName"
						@row-dblclick="addBomMatFromDialog">
						<el-table-column type="index" label="#" width="44" align="center" />
						<el-table-column prop="name" label="物料名称" min-width="160" show-overflow-tooltip />
						<el-table-column prop="code" label="编码" width="100" show-overflow-tooltip>
							<template slot-scope="{ row }">{{ row.code || '—' }}</template>
						</el-table-column>
						<el-table-column label="划分" width="72" align="center">
							<template slot-scope="{ row }">
								{{ row.stock_class_text || (row.stock_class === 'raw' ? '原料' : '成品') }}
							</template>
						</el-table-column>
						<el-table-column prop="unit" label="单位" width="56" align="center">
							<template slot-scope="{ row }">{{ row.unit || '—' }}</template>
						</el-table-column>
						<el-table-column label="损耗%" width="64" align="center">
							<template slot-scope="{ row }">
								<span :class="{ 'fsm-bom-loss': Number(row.loss_rate) > 0 }">
									{{ formatBomLossRate(row.loss_rate) }}
								</span>
							</template>
						</el-table-column>
						<el-table-column label="状态" width="72" align="center">
							<template slot-scope="{ row }">
								<span v-if="bomMatTargetHas(row.id)" class="fsm-bom-mat-status is-added">已加入</span>
								<span v-else class="fsm-bom-mat-status">可选</span>
							</template>
						</el-table-column>
						<el-table-column label="操作" width="72" align="center" fixed="right">
							<template slot-scope="{ row }">
								<el-button
									type="text"
									:disabled="bomMatTargetHas(row.id)"
									@click.stop="addBomMatFromDialog(row)">
									{{ bomMatTargetHas(row.id) ? '已加入' : '加入' }}
								</el-button>
							</template>
						</el-table-column>
					</el-table>
					<el-empty
						v-else
						description="暂无物料，请先在「物料库存」维护"
						:image-size="64" />
				</div>
			</div>
			<span slot="footer">
				<span class="fsm-pick__footer-meta">共 {{ bomMatLibrary.length }} 项可点选</span>
				<el-button type="primary" @click="bomMatDialogVisible = false">完成</el-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
import Sortable from 'sortablejs';
import { buildCartGoodsStats } from '@/utils/cart-stats';

export default {
	name: 'FactoryStockManage',
	beforeDestroy() {
		this.destroyMatSpecSortables();
	},
	props: {
		/** 父页 pm-section-bar 选中的一级分类 */
		rootPid: {
			type: [Number, String],
			default: 0,
		},
		rootName: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			tab: 'materials',
			matCategories: [],
			matCategoryTab: 'all',
			matCatTreeVisible: true,
			matCatDialog: false,
			matCatFormDialog: false,
			matCatSaving: false,
			matCatForm: { id: 0, name: '', sort: 0 },
			matCatMoving: false,
			matLoading: false,
			matSearch: '',
			matStockClass: '',
			matPage: 1,
			matLimit: 10,
			matTotal: 0,
			matMaterialTotal: 0,
			matGroups: [],
			matOptions: [],
			/** 物料大项（灯条/灯带挂配方） */
			matHeaderOptions: [],
			/** 规格型号可选灯条宽度（可手输） */
			bomStripWidthOptions: ['5mm', '8mm'],
			/** 大项双击 · 规格组合编辑器 */
			matSpecEditor: {
				visible: false,
				loading: false,
				saving: false,
				headerId: 0,
				goodsName: '',
				group: null,
				header: null,
				dimensions: [],
				combos: [],
				_uid: 1,
			},
			matDialog: false,
			matSaving: false,
			/** goods=商品大项 | node=添加子级规格（无限级） | edit=编辑规格 */
			matFormMode: 'goods',
			matForm: {
				id: 0,
				code: '',
				name: '',
				stock_class: 'finished',
				unit: '个',
				stock_length: '',
				loss_rate: '',
				remark: '',
				goods_id: null,
				category_id: null,
				goods_name: '',
				spec_name: '',
				parent_id: null,
				parent_path: '',
			},
			inDialog: false,
			inForm: { material_id: null, type: 'purchase_in', qty: '', remark: '' },
			inSaving: false,
			demandLoading: false,
			demandOrderLoading: false,
			demandDateRange: [],
			demandOrders: [],
			demandOrdersQueried: false,
			demandSelectedIds: [],
			demandActiveId: 0,
			demandSummary: {
				order_count: 0,
				amount_total: 0,
				amount_total_text: '0',
				line_total: 0,
				goods_qty_total: 0,
			},
			demandDetailGoods: [],
			demandDetailLoading: false,
			demandDetailQueried: false,
			demandList: [],
			demandOrderCount: 0,
			demandQueried: false,
			bomMatSearch: '',
			bomWorkspace: [],
			bomWorkspaceLoading: false,
			bomMatDialogVisible: false,
			bomMatTargetId: 0,
			/** { mode:'common' } | { mode:'param', parameter_pid, parameter_id, option_name } */
			bomMatTargetCtx: { mode: 'common' },
			bomPickVisible: false,
			bomPickLoading: false,
			bomPickSaving: false,
			bomPickSearch: '',
			bomPickTypes: [{ id: '', name: '全部分类' }],
			bomPickTypeIndex: 0,
			bomPickGoodList: [],
			bomPickSelectedGood: null,
			bomTplVisible: false,
			bomTplLoading: false,
			bomTplSaving: false,
			bomTplList: [],
			bomTplCode: 'strip_process',
			bomTplPendingGood: null,
			bomGoodDetailVisible: false,
			bomGood: null,
			bomSkuActive: -1,
			bomSpecIds: [],
			ledgerLoading: false,
			ledgerType: '',
			ledgerPage: 1,
			ledgerLimit: 20,
			ledgerTotal: 0,
			ledgerList: [],
		};
	},
	computed: {
		activeRootId() {
			return Number(this.rootPid) || 0;
		},
		currentMatCategoryLabel() {
			if (this.matCategoryTab === 'all') return '全部';
			if (this.matCategoryTab === 'none') return '未分类';
			const c = (this.matCategories || []).find((x) => String(x.id) === String(this.matCategoryTab));
			return (c && c.name) || '未分类';
		},
		/** 当前选中的自定义工厂分类（全部/未分类时为 null） */
		selectedMatCategory() {
			const tab = String(this.matCategoryTab || '');
			if (tab === 'all' || tab === 'none') return null;
			const id = Number(tab);
			if (!(id > 0)) return null;
			return (this.matCategories || []).find((x) => Number(x.id) === id) || null;
		},
		matDialogTitle() {
			if (this.matFormMode === 'edit') {
				const remark = String(this.matForm.remark || '').trim();
				return remark === '__FSM_GOODS_HEADER__' ? '编辑物料大项' : '编辑物料产品';
			}
			if (this.matFormMode === 'node') return '添加规格值（新物料产品）';
			return '添加物料大项';
		},
		matFormEditRemarkLocked() {
			if (this.matFormMode !== 'edit') return false;
			const remark = String(this.matForm.remark || '').trim();
			return remark === '__FSM_SPEC_PREFIX__' || remark === '__FSM_GOODS_HEADER__';
		},
		demandSelectedOrders() {
			const set = {};
			(this.demandSelectedIds || []).forEach((id) => { set[id] = true; });
			return (this.demandOrders || []).filter((o) => set[o.id]);
		},
		demandSelectedLineTotal() {
			return this.demandSelectedOrders.reduce((s, o) => s + (Number(o.line_count) || 0), 0);
		},
		demandSelectedGoodsQty() {
			return this.demandSelectedOrders.reduce((s, o) => s + (Number(o.goods_qty) || 0), 0);
		},
		bomMatLibrary() {
			const q = String(this.bomMatSearch || '').trim().toLowerCase();
			return (this.matOptions || []).filter((m) => {
				if (!q) return true;
				const hay = [m.code, m.name, m.label].join(' ').toLowerCase();
				return hay.includes(q);
			});
		},
		bomConfiguredCount() {
			return (this.bomWorkspace || []).filter((x) => this.bomLinkedLineCount(x) > 0).length;
		},
		bomMatDialogTitle() {
			const ctx = this.bomMatTargetCtx || { mode: 'common' };
			if (ctx.mode === 'param' || ctx.mode === 'sku_combo' || ctx.mode === 'sku') {
				return `关联物料 · ${ctx.option_name || '选项'}`;
			}
			return '关联公共物料';
		},
		demandSelectedAmountText() {
			const sum = this.demandSelectedOrders.reduce((s, o) => s + (Number(o.total) || 0), 0);
			return String(Math.round(sum * 100) / 100);
		},
		demandActiveOrder() {
			const id = Number(this.demandActiveId) || 0;
			if (!id) return null;
			return (this.demandOrders || []).find((o) => Number(o.id) === id) || null;
		},
		/** 与 factoryDetail 页 printGoodsStats 同源 */
		demandOrderGoodsStats() {
			return buildCartGoodsStats(this.demandDetailGoods || []);
		},
		/**
		 * 统一左→右规格表：表头在列表上方；商品大项与同级规格值纵向合并（树状）。
		 */
		matFlatTable() {
			const groups = this.matGroups || [];
			const prepared = groups.map((g) => {
				const specTable = this.buildMatSpecTable(g);
				return { group: g, specTable };
			});
			const maxDepth = prepared.reduce((m, p) => {
				const d = (p.specTable.rows || []).reduce(
					(mm, r) => Math.max(mm, (r.specValues || []).length),
					0,
				);
				return Math.max(m, d);
			}, 0);
			const dimCount = Math.max(maxDepth, 1);
			const dimensions = Array.from({ length: dimCount }, (_, i) => `规格${i + 1}`);
			const blankSpecMeta = () => ({
				specShow: Array.from({ length: dimCount }, () => true),
				specRowspan: Array.from({ length: dimCount }, () => 1),
			});
			const rows = [];
			prepared.forEach((p, gIdx) => {
				const group = p.group;
				const list = p.specTable.rows || [];
				if (!list.length) {
					rows.push(Object.assign({
						rowKey: 'empty-' + gIdx + '-' + (group.goods_id || group.goods_name || 'x'),
						group,
						item: null,
						specValues: [],
						specText: '',
						showGoods: true,
						rowspan: 1,
						isEmptyGoods: true,
						isGoodsOnly: false,
					}, blankSpecMeta()));
					return;
				}
				const groupRows = list.map((r, i) => ({
					rowKey: 'm-' + r.item.id,
					group,
					item: r.item,
					specValues: r.specValues || [],
					specText: r.specText || '',
					hasChildren: !!r.hasChildren,
					showGoods: i === 0,
					rowspan: i === 0 ? list.length : 0,
					isEmptyGoods: false,
					isGoodsOnly: !!r.isGoodsOnly,
					specShow: Array.from({ length: dimCount }, () => true),
					specRowspan: Array.from({ length: dimCount }, () => 1),
				}));
				// 同组内：上级规格路径相同则纵向合并（如多个 5mm / xxx）
				this.applyMatSpecTreeMerge(groupRows, dimCount);
				groupRows.forEach((r) => rows.push(r));
			});
			return { dimensions, rows };
		},
	},
	watch: {
		rootPid: {
			immediate: true,
			handler(val, oldVal) {
				const pid = Number(val) || 0;
				const prev = Number(oldVal) || 0;
				if (!pid) {
					this.matGroups = [];
					this.matTotal = 0;
					this.matMaterialTotal = 0;
					this.bomWorkspace = [];
					return;
				}
				if (pid !== prev || !oldVal) {
					this.matSearch = '';
					this.matPage = 1;
					this.loadMaterials();
					this.loadMatOptions();
					if (this.tab === 'bom') {
						this.ensureBomDataReset().then(() => this.reloadBomTab());
					}
				}
			},
		},
	},
	mounted() {
		this.matCatTreeVisible = this.readMatCatTreeVisible();
		this.loadMatCategories(false);
		this.loadMatOptions();
		this.initDemandDateRange();
		if (this.activeRootId) this.loadMaterials();
		// 清理旧版工作区 key；正式清空在首次进入 SKU 配方时执行
		try {
			localStorage.removeItem('fsm_bom_workspace_ids');
		} catch (e) { /* ignore */ }
	},
	methods: {
		matCatTreeVisibleKey() {
			return 'fsm_mat_cat_tree_visible';
		},
		readMatCatTreeVisible() {
			try {
				const raw = localStorage.getItem(this.matCatTreeVisibleKey());
				if (raw === null || raw === undefined || raw === '') return true;
				return raw !== '0' && raw !== 'false';
			} catch (e) {
				return true;
			}
		},
		setMatCatTreeVisible(visible) {
			this.matCatTreeVisible = !!visible;
			try {
				localStorage.setItem(this.matCatTreeVisibleKey(), this.matCatTreeVisible ? '1' : '0');
			} catch (e) { /* ignore */ }
		},
		selectMatCategory(tab) {
			const next = String(tab || 'all');
			if (this.matCategoryTab === next) return;
			this.matCategoryTab = next;
			this.onMatCategoryTab();
		},
		loadMatCategories(reloadList) {
			const that = this;
			return that.$fetch('addons/hlm/api.FactoryStock/materialCategoryList').then((res) => {
				if (res.code == 1) {
					that.matCategories = res.data || [];
					const tab = that.matCategoryTab;
					if (tab !== 'all' && tab !== 'none') {
						const ok = that.matCategories.some((c) => String(c.id) === String(tab));
						if (!ok) that.matCategoryTab = 'all';
					}
				}
				if (reloadList) that.loadMaterials();
			});
		},
		onMatCategoryTab() {
			this.matPage = 1;
			this.loadMaterials();
			this.loadMatOptions();
		},
		openMatCatManage() {
			this.matCatDialog = true;
			this.loadMatCategories(false);
		},
		resetMatCatForm() {
			this.matCatForm = { id: 0, name: '', sort: this.matCategories.length };
		},
		openMatCatAdd() {
			this.matCatForm = {
				id: 0,
				name: '',
				sort: (this.matCategories || []).length,
			};
			this.matCatFormDialog = true;
		},
		openMatCatEdit(row) {
			const target = row && row.id ? row : this.selectedMatCategory;
			if (!target || !target.id) {
				this.$message.warning('请先选择要编辑的自定义分类');
				return;
			}
			this.matCatForm = {
				id: Number(target.id),
				name: String(target.name || ''),
				sort: Number(target.sort) || 0,
			};
			this.matCatFormDialog = true;
		},
		saveMatCatForm() {
			const that = this;
			const name = String(that.matCatForm.name || '').trim();
			if (!name) {
				that.$message.warning('请填写分类名称');
				return;
			}
			const id = Number(that.matCatForm.id) || 0;
			const sort = Number(that.matCatForm.sort);
			that.matCatSaving = true;
			that.$post('addons/hlm/api.FactoryStock/materialCategorySave', {
				id,
				name,
				sort: Number.isFinite(sort) ? sort : that.matCategories.length,
			})
				.then((res) => {
					if (res.code == 1) {
						that.$message.success(res.msg || '已保存');
						that.matCatFormDialog = false;
						const newId = res.data && res.data.id ? Number(res.data.id) : id;
						that.loadMatCategories(true).then(() => {
							if (!id && newId > 0) that.selectMatCategory(String(newId));
						});
					}
				})
				.finally(() => {
					that.matCatSaving = false;
				});
		},
		canMoveMatCat(row, delta) {
			const list = this.matCategories || [];
			const idx = list.findIndex((x) => Number(x.id) === Number(row && row.id));
			if (idx < 0) return false;
			const next = idx + Number(delta);
			return next >= 0 && next < list.length;
		},
		moveMatCategory(row, delta) {
			const that = this;
			if (!row || !row.id || that.matCatMoving) return;
			const list = (that.matCategories || []).slice();
			const idx = list.findIndex((x) => Number(x.id) === Number(row.id));
			const swapIdx = idx + Number(delta);
			if (idx < 0 || swapIdx < 0 || swapIdx >= list.length) return;
			const a = list[idx];
			const b = list[swapIdx];
			// 用目标下标写入 sort，避免同 sort 值交换后顺序不变
			that.matCatMoving = true;
			Promise.all([
				that.$post('addons/hlm/api.FactoryStock/materialCategorySave', {
					id: a.id,
					name: a.name,
					sort: swapIdx,
				}),
				that.$post('addons/hlm/api.FactoryStock/materialCategorySave', {
					id: b.id,
					name: b.name,
					sort: idx,
				}),
			])
				.then((results) => {
					if (results.every((r) => r && r.code == 1)) {
						that.loadMatCategories(false);
					}
				})
				.finally(() => {
					that.matCatMoving = false;
				});
		},
		delMatCategory(row) {
			if (!row || !row.id) {
				this.$message.warning('请先选择要删除的自定义分类');
				return;
			}
			this.$confirm(`删除分类「${row.name}」？分类下若仍有物料将无法删除。`, '提示', { type: 'warning' })
				.then(() => {
					this.$post('addons/hlm/api.FactoryStock/materialCategoryDel', { id: row.id }).then((res) => {
						if (res.code == 1) {
							this.$message.success(res.msg || '已删除');
							if (String(this.matCategoryTab) === String(row.id)) {
								this.matCategoryTab = 'all';
								this.onMatCategoryTab();
							}
							this.loadMatCategories(true);
						}
					});
				})
				.catch(() => {});
		},
		initDemandDateRange() {
			const end = new Date();
			end.setHours(0, 0, 0, 0);
			const start = new Date(end);
			start.setDate(start.getDate() - 6);
			this.demandDateRange = [start.getTime(), end.getTime()];
		},
		demandRangeUnix() {
			const range = this.demandDateRange || [];
			if (!range[0] || !range[1]) return null;
			const start = Math.floor(Number(range[0]) / 1000);
			const endDay = new Date(Number(range[1]));
			endDay.setHours(23, 59, 59, 999);
			const end = Math.floor(endDay.getTime() / 1000);
			if (!(start > 0) || !(end > 0)) return null;
			return { start_time: start, end_time: end };
		},
		/**
		 * 同组叶子行：按规格路径前缀合并单元格（树状 rowspan）。
		 * 例：5mm/3000k、5mm/4000K → 规格1「5mm」合并两行。
		 */
		applyMatSpecTreeMerge(groupRows, dimCount) {
			const n = (groupRows || []).length;
			if (!n || !(dimCount > 0)) return;
			const valAt = (row, di) => String((row.specValues && row.specValues[di]) || '');
			const samePrefix = (a, b, di) => {
				for (let t = 0; t <= di; t += 1) {
					if (valAt(a, t) !== valAt(b, t)) return false;
				}
				return true;
			};
			for (let di = 0; di < dimCount; di += 1) {
				let i = 0;
				while (i < n) {
					const cur = groupRows[i];
					const v = valAt(cur, di);
					if (!v) {
						cur.specShow[di] = true;
						cur.specRowspan[di] = 1;
						i += 1;
						continue;
					}
					let j = i + 1;
					while (j < n && samePrefix(cur, groupRows[j], di)) j += 1;
					const span = j - i;
					for (let k = i; k < j; k += 1) {
						groupRows[k].specShow[di] = k === i;
						groupRows[k].specRowspan[di] = k === i ? span : 0;
					}
					i = j;
				}
			}
		},
		/**
		 * 规格组合表：只展示叶子物料（完整规格组合）。
		 * 无规格时：物料大项占位行本身即为可库存叶子。
		 */
		buildMatSpecTable(group) {
			const allItems = (group && group.items || []).filter((it) => it && it.id != null);
			const headerItems = allItems.filter((it) => it.is_goods_header);
			const headerItem = headerItems[0] || null;
			const items = allItems.filter((it) => !it.is_goods_header);
			if (!items.length) {
				// 仅物料大项：每条占位行单独展示（避免同名大项被折叠成一行）
				if (headerItems.length) {
					const rows = headerItems.map((h) => ({
						item: h,
						specValues: [],
						specText: '',
						depth: 0,
						hasChildren: false,
						isGoodsOnly: true,
					}));
					return { rows, allRows: rows, productCount: rows.length };
				}
				return { rows: [], productCount: 0, allRows: [] };
			}
			const byId = {};
			items.forEach((n) => { byId[n.id] = n; });
			const headerId = headerItem ? Number(headerItem.id) : 0;
			const children = {};
			const roots = [];
			items.forEach((n) => {
				const pid = Number(n.parent_id) || 0;
				// 父级是物料大项占位行时，视为顶级规格根（大项本身不参与规格路径）
				const parentIsHeader = headerId > 0 && pid === headerId;
				const parentIsSpec = pid > 0 && !parentIsHeader && !!byId[pid];
				if (parentIsSpec) {
					if (!children[pid]) children[pid] = [];
					children[pid].push(n);
				} else {
					roots.push(n);
				}
			});
			const sortFn = (a, b) => {
				const c = String(a.seg_name || a.name || '').localeCompare(
					String(b.seg_name || b.name || ''),
					'zh',
					{ numeric: true, sensitivity: 'base' },
				);
				return c !== 0 ? c : Number(a.id) - Number(b.id);
			};
			roots.sort(sortFn);
			Object.keys(children).forEach((k) => children[k].sort(sortFn));

			const allRows = [];
			const walk = (node, pathSegs) => {
				const seg = this.matSpecSegLabel(node, group);
				const specValues = seg ? pathSegs.concat(seg) : pathSegs.slice();
				const kids = children[node.id] || [];
				const row = {
					item: node,
					specValues,
					specText: specValues.join(' / '),
					depth: Math.max(0, specValues.length - 1),
					hasChildren: kids.length > 0,
					isGoodsOnly: false,
				};
				allRows.push(row);
				kids.forEach((child) => walk(child, specValues));
			};
			roots.forEach((r) => walk(r, []));

			// 仅叶子：每一行都是可独立库存的完整规格组合
			const rows = allRows.filter((r) => !r.hasChildren);

			return {
				rows,
				allRows,
				productCount: rows.length,
			};
		},
		matSpecSegLabel(node, group) {
			const seg = String(node.seg_name || node.sku_label || '').trim();
			if (seg) return seg;
			const name = String(node.name || '').trim();
			const gname = String((group && group.goods_name) || '').trim();
			if (gname && name.indexOf(gname) === 0) {
				return name.slice(gname.length).replace(/^[\s\-_/|，,]+/, '').trim();
			}
			return name;
		},
		matSpecPathLabel(group, node) {
			if (!node || !group) return '';
			const table = this.buildMatSpecTable(group);
			const hit = (table.allRows || table.rows || []).find(
				(r) => r.item && Number(r.item.id) === Number(node.id),
			);
			if (hit && hit.specText) return hit.specText;
			return this.matSpecSegLabel(node, group);
		},
		onMatRowDelete(group, row) {
			if (!row || !row.item) return;
			this.delSpecNode(group, row.item);
		},
		matSpecNextTempId(prefix) {
			const n = (this.matSpecEditor._uid || 1) + 1;
			this.matSpecEditor._uid = n;
			return String(prefix || 't') + n;
		},
		blankMatSpecEditor() {
			return {
				visible: false,
				loading: false,
				saving: false,
				headerId: 0,
				goodsName: '',
				group: null,
				header: null,
				dimensions: [],
				combos: [],
				_uid: 1,
			};
		},
		resetMatSpecEditor() {
			const keepUid = this.matSpecEditor._uid || 1;
			this.matSpecEditor = Object.assign(this.blankMatSpecEditor(), { _uid: keepUid });
		},
		onMatSpecEditorClosed() {
			this.destroyMatSpecSortables();
			this.resetMatSpecEditor();
		},
		scheduleMatSpecSortables() {
			this.$nextTick(() => {
				this.initMatSpecSortables();
			});
		},
		destroyMatSpecSortables() {
			if (this._matSpecDimSortable) {
				try { this._matSpecDimSortable.destroy(); } catch (e) { /* ignore */ }
				this._matSpecDimSortable = null;
			}
			(this._matSpecValueSortables || []).forEach((s) => {
				try { s.destroy(); } catch (e) { /* ignore */ }
			});
			this._matSpecValueSortables = [];
			this._matSpecSortSignature = '';
		},
		matSpecSortSignature() {
			return (this.matSpecEditor.dimensions || [])
				.map((d) => `${d.temp_id}:${(d.children || []).map((c) => c.temp_id).join(',')}`)
				.join('|');
		},
		initMatSpecSortables() {
			if (!this.matSpecEditor.visible) return;
			const listEl = this.$refs.matSpecDimsEl;
			if (!listEl) return;
			const signature = this.matSpecSortSignature();
			if (signature === this._matSpecSortSignature && this._matSpecDimSortable) return;
			this.destroyMatSpecSortables();
			this._matSpecSortSignature = signature;
			this._matSpecDimSortable = Sortable.create(listEl, {
				animation: 160,
				handle: '.fsm-mat-spec-drag',
				draggable: '.fsm-mat-spec-dim',
				ghostClass: 'fsm-mat-spec-dim--ghost',
				chosenClass: 'fsm-mat-spec-dim--chosen',
				onEnd: (evt) => this.onMatSpecDimSortEnd(evt),
			});
			this._matSpecValueSortables = [];
			listEl.querySelectorAll('.fsm-mat-spec-values-list').forEach((el) => {
				const sortable = Sortable.create(el, {
					animation: 160,
					handle: '.fsm-mat-spec-value-drag',
					draggable: '.fsm-mat-spec-value',
					ghostClass: 'fsm-mat-spec-value--ghost',
					chosenClass: 'fsm-mat-spec-value--chosen',
					onEnd: (evt) => this.onMatSpecValueSortEnd(evt),
				});
				this._matSpecValueSortables.push(sortable);
			});
		},
		/**
		 * Sortable 会先改 DOM，再交给 Vue 按 key 重绘时（尤其拖到第 0 位）容易不同步。
		 * 先把节点放回 oldIndex，再改数据让 Vue 完整重渲染。
		 */
		revertSortableDomMove(evt) {
			const item = evt && evt.item;
			const from = evt && evt.from;
			const oldIndex = evt && evt.oldIndex;
			if (!item || !from || oldIndex == null) return;
			if (item.parentNode === from) {
				from.removeChild(item);
			}
			const ref = from.children[oldIndex] || null;
			from.insertBefore(item, ref);
		},
		onMatSpecDimSortEnd(evt) {
			const { oldIndex, newIndex } = evt;
			if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
			this.revertSortableDomMove(evt);
			const dims = (this.matSpecEditor.dimensions || []).slice();
			if (!(oldIndex >= 0) || !(newIndex >= 0) || oldIndex >= dims.length) return;
			const moved = dims.splice(oldIndex, 1)[0];
			if (!moved) return;
			dims.splice(newIndex, 0, moved);
			this.matSpecEditor.dimensions = dims;
			(this.matSpecEditor.combos || []).forEach((row) => {
				const vals = Array.isArray(row.spec_values) ? row.spec_values.slice() : [];
				while (vals.length < dims.length) vals.push('');
				const movedVal = vals.splice(oldIndex, 1)[0];
				vals.splice(newIndex, 0, movedVal);
				row.spec_values = vals;
			});
			this.rebuildMatSpecCombos();
			// 强制按新顺序重建 Sortable，避免旧 DOM 绑定
			this.destroyMatSpecSortables();
			this.scheduleMatSpecSortables();
		},
		onMatSpecValueSortEnd(evt) {
			const { oldIndex, newIndex } = evt;
			if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
			this.revertSortableDomMove(evt);
			const tempId = String(
				(evt.from && evt.from.dataset && evt.from.dataset.dimTempId) || '',
			);
			const dimIndex = (this.matSpecEditor.dimensions || []).findIndex(
				(d) => String(d.temp_id) === tempId,
			);
			if (dimIndex < 0) return;
			const dim = this.matSpecEditor.dimensions[dimIndex];
			if (!dim || !dim.children) return;
			const children = dim.children.slice();
			if (!(oldIndex >= 0) || !(newIndex >= 0) || oldIndex >= children.length) return;
			const moved = children.splice(oldIndex, 1)[0];
			if (!moved) return;
			children.splice(newIndex, 0, moved);
			this.$set(this.matSpecEditor.dimensions[dimIndex], 'children', children);
			this.rebuildMatSpecCombos();
			this.destroyMatSpecSortables();
			this.scheduleMatSpecSortables();
		},
		openMatSpecEditor(group) {
			if (!group) return;
			const headers = (group.items || []).filter((it) => it && it.is_goods_header);
			const header = headers[0] || null;
			if (!header || !(Number(header.id) > 0)) {
				this.$message.warning('未找到物料大项，请先添加物料大项');
				return;
			}
			this.matSpecEditor.visible = true;
			this.matSpecEditor.loading = true;
			this.matSpecEditor.headerId = Number(header.id);
			this.matSpecEditor.goodsName = group.goods_name || header.name || '';
			this.matSpecEditor.group = group;
			this.$fetch('addons/hlm/api.FactoryStock/materialSpecEditorGet', {
				header_id: header.id,
			})
				.then((res) => {
					if (res.code != 1) return;
					const data = res.data || {};
					this.matSpecEditor.header = data.header || null;
					const dims = (data.dimensions || []).map((d, i) => ({
						temp_id: d.temp_id || this.matSpecNextTempId('d'),
						name: d.name || ('规格' + (i + 1)),
						children: (d.children || []).map((c) => ({
							temp_id: c.temp_id || this.matSpecNextTempId('v'),
							name: c.name || '',
						})),
					}));
					this.matSpecEditor.dimensions = dims;
					const h = data.header || {};
					this.matSpecEditor.combos = (data.combos || []).map((c) => ({
						id: Number(c.id) || 0,
						spec_values: Array.isArray(c.spec_values) ? c.spec_values.slice() : [],
						code: c.code || '',
						unit: c.unit || h.unit || '个',
						stock_class: c.stock_class === 'finished' ? 'finished' : 'raw',
						stock_length: c.stock_length != null ? String(c.stock_length) : '',
						loss_rate: c.loss_rate != null && Number(c.loss_rate) > 0 ? String(c.loss_rate) : '',
						qty: c.qty,
						status: c.status === 'down' ? 'down' : 'up',
					}));
					if (!dims.length && !(data.combos || []).length) {
						// 空大项：给一个默认维度便于录入
						this.matSpecEditor.dimensions = [];
					}
				})
				.finally(() => {
					this.matSpecEditor.loading = false;
					this.scheduleMatSpecSortables();
				});
		},
		addMatSpecDim() {
			this.matSpecEditor.dimensions.push({
				temp_id: this.matSpecNextTempId('d'),
				name: '规格' + (this.matSpecEditor.dimensions.length + 1),
				children: [],
			});
			this.scheduleMatSpecSortables();
		},
		removeMatSpecDim(di) {
			const dim = this.matSpecEditor.dimensions[di];
			const name = (dim && dim.name) || ('规格' + (di + 1));
			this.$confirm(`删除规格「${name}」将重建组合表，确认？`, '提示', { type: 'warning' })
				.then(() => {
					this.matSpecEditor.dimensions.splice(di, 1);
					this.rebuildMatSpecCombos();
					this.scheduleMatSpecSortables();
				})
				.catch(() => {});
		},
		addMatSpecValue(di) {
			const dim = this.matSpecEditor.dimensions[di];
			if (!dim) return;
			if ((dim.children || []).some((c) => !String(c.name || '').trim())) {
				this.$message.warning('请先填写已有空规格值');
				return;
			}
			this.$prompt('请输入规格值', '添加规格值', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				inputPattern: /\S+/,
				inputErrorMessage: '规格值不能为空',
			})
				.then(({ value }) => {
					const name = String(value || '').trim();
					if (!name) return;
					if ((dim.children || []).some((c) => String(c.name || '').trim() === name)) {
						this.$message.warning('规格值已存在');
						return;
					}
					dim.children.push({ temp_id: this.matSpecNextTempId('v'), name });
					this.rebuildMatSpecCombos();
					this.scheduleMatSpecSortables();
				})
				.catch(() => {});
		},
		editMatSpecValue(di, ci) {
			const dim = this.matSpecEditor.dimensions[di];
			const ch = dim && dim.children && dim.children[ci];
			if (!ch) return;
			this.$prompt('修改规格值', '编辑', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				inputValue: ch.name || '',
				inputPattern: /\S+/,
				inputErrorMessage: '规格值不能为空',
			})
				.then(({ value }) => {
					const name = String(value || '').trim();
					if (!name) return;
					ch.name = name;
					this.rebuildMatSpecCombos();
					this.scheduleMatSpecSortables();
				})
				.catch(() => {});
		},
		removeMatSpecValue(di, ci) {
			const dim = this.matSpecEditor.dimensions[di];
			if (!dim) return;
			dim.children.splice(ci, 1);
			this.rebuildMatSpecCombos();
			this.scheduleMatSpecSortables();
		},
		/**
		 * 规格组合表：左→右维度列 + 相同前缀纵向合并（与物料列表一致）
		 */
		matSpecEditorComboTable() {
			const dims = this.matSpecEditor.dimensions || [];
			const combos = this.matSpecEditor.combos || [];
			const dimCount = Math.max(dims.length, 1);
			const dimensions = Array.from(
				{ length: dimCount },
				(_, i) => (dims[i] && dims[i].name) || `规格${i + 1}`,
			);
			const rows = combos.map((combo, idx) => {
				const raw = Array.isArray(combo.spec_values) ? combo.spec_values : [];
				const specValues = Array.from({ length: dimCount }, (_, i) => String(raw[i] || ''));
				return {
					rowKey: 'msc-' + (combo.id || 0) + '-' + idx + '-' + specValues.join('/'),
					combo,
					specValues,
					specShow: Array.from({ length: dimCount }, () => true),
					specRowspan: Array.from({ length: dimCount }, () => 1),
				};
			});
			this.applyMatSpecTreeMerge(rows, dimCount);
			return { dimensions, rows, dimCount };
		},
		rebuildMatSpecCombos() {
			const dims = this.matSpecEditor.dimensions || [];
			const valueLists = dims.map((d) =>
				(d.children || []).map((c) => String(c.name || '').trim()).filter(Boolean),
			);
			if (!valueLists.length || valueLists.some((v) => !v.length)) {
				this.matSpecEditor.combos = [];
				return;
			}
			const prevByKey = {};
			(this.matSpecEditor.combos || []).forEach((row) => {
				const key = (row.spec_values || []).join('\u0001');
				prevByKey[key] = row;
			});
			const h = this.matSpecEditor.header || {};
			const out = [];
			const walk = (depth, acc) => {
				if (depth >= valueLists.length) {
					const vals = acc.slice();
					const key = vals.join('\u0001');
					const prev = prevByKey[key];
					if (prev) {
						prev.spec_values = vals;
						out.push(prev);
					} else {
						out.push({
							id: 0,
							spec_values: vals,
							code: '',
							unit: h.unit || '个',
							stock_class: h.stock_class === 'finished' ? 'finished' : 'raw',
							stock_length: h.stock_length != null ? String(h.stock_length) : '',
							loss_rate: h.loss_rate != null && Number(h.loss_rate) > 0 ? String(h.loss_rate) : '',
							qty: null,
							status: 'up',
						});
					}
					return;
				}
				valueLists[depth].forEach((v) => {
					acc[depth] = v;
					walk(depth + 1, acc);
				});
			};
			walk(0, []);
			this.matSpecEditor.combos = out;
		},
		saveMatSpecEditor() {
			const that = this;
			const headerId = Number(that.matSpecEditor.headerId || 0);
			if (!(headerId > 0)) return;
			const dimensions = (that.matSpecEditor.dimensions || []).map((d) => ({
				name: String(d.name || '').trim(),
				children: (d.children || [])
					.map((c) => ({ name: String(c.name || '').trim() }))
					.filter((c) => c.name),
			})).filter((d) => d.name || (d.children && d.children.length));
			const combos = (that.matSpecEditor.combos || []).map((c) => ({
				id: Number(c.id) || 0,
				spec_values: Array.isArray(c.spec_values) ? c.spec_values : [],
				code: c.code || '',
				unit: c.unit || '个',
				stock_class: c.stock_class === 'finished' ? 'finished' : 'raw',
				stock_length: c.stock_length,
				loss_rate: c.loss_rate,
				status: c.status === 'down' ? 'down' : 'up',
			}));
			that.matSpecEditor.saving = true;
			that
				.$post('addons/hlm/api.FactoryStock/materialSpecEditorSave', {
					header_id: headerId,
					dimensions: JSON.stringify(dimensions),
					combos: JSON.stringify(combos),
				})
				.then((res) => {
					if (res.code == 1) {
						that.$message.success(res.msg || '已保存');
						that.matSpecEditor.visible = false;
						that.loadMaterials();
						that.loadMatOptions();
					}
				})
				.finally(() => {
					that.matSpecEditor.saving = false;
				});
		},
		delMatSpecEditorGroup() {
			const group = this.matSpecEditor.group;
			if (!group) return;
			this.$confirm(`确认删除物料大项「${group.goods_name || ''}」及其全部规格？`, '提示', {
				type: 'warning',
			})
				.then(() => {
					this.matSpecEditor.visible = false;
					this.delGoodsGroup(group);
				})
				.catch(() => {});
		},
		onTab() {
			if (this.tab === 'demand') {
				if (!this.demandDateRange || !this.demandDateRange.length) this.initDemandDateRange();
				if (!this.demandOrdersQueried) this.loadDemandOrders();
			}
			if (this.tab === 'ledger') this.loadLedger();
			if (this.tab === 'bom') {
				this.ensureBomDataReset().then(() => this.reloadBomTab());
			}
			if (this.tab === 'materials') this.loadMaterials();
		},
		openInbound(row) {
			this.inForm = {
				material_id: row && row.id ? row.id : null,
				type: 'purchase_in',
				qty: '',
				remark: '',
			};
			this.inDialog = true;
		},
		formatTime(ts) {
			const n = Number(ts);
			if (!n) return '—';
			const d = new Date(n * 1000);
			const pad = (v) => String(v).padStart(2, '0');
			return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
		},
		reloadMaterials() {
			this.matPage = 1;
			this.loadMaterials();
		},
		loadMatOptions() {
			const that = this;
			const params = {};
			const pid = that.activeRootId;
			if (pid) params.pid = pid;
			if (that.matCategoryTab === 'none') {
				params.uncategorized = '1';
			} else if (that.matCategoryTab !== 'all') {
				const cid = Number(that.matCategoryTab);
				if (cid > 0) params.category_id = cid;
			}
			that.$fetch('addons/hlm/api.FactoryStock/materialOptions', params).then((res) => {
				if (res.code == 1) that.matOptions = res.data || [];
			});
			that.loadMatHeaderOptions();
		},
		loadMatHeaderOptions() {
			const that = this;
			return that.$fetch('addons/hlm/api.FactoryStock/materialHeaderOptions', {}).then((res) => {
				if (res.code == 1) that.matHeaderOptions = res.data || [];
				return that.matHeaderOptions;
			});
		},
		loadMaterials() {
			const that = this;
			const pid = that.activeRootId;
			if (!pid) return;
			that.matLoading = true;
			const params = {
				page: that.matPage,
				limit: that.matLimit,
				search: that.matSearch,
				pid,
			};
			if (that.matStockClass) params.stock_class = that.matStockClass;
			if (that.matCategoryTab === 'none') {
				params.uncategorized = '1';
			} else if (that.matCategoryTab !== 'all') {
				const cid = Number(that.matCategoryTab);
				if (cid > 0) params.category_id = cid;
			}
			that.$fetch('addons/hlm/api.FactoryStock/materialList', params)
				.then((res) => {
					if (res.code == 1) {
						that.matGroups = (res.data && res.data.groups) || [];
						that.matTotal = (res.data && res.data.total) || 0;
						that.matMaterialTotal = (res.data && res.data.material_total) || 0;
					}
				})
				.finally(() => {
					that.matLoading = false;
				});
		},
		blankMatForm() {
			let categoryId = null;
			if (this.matCategoryTab !== 'all' && this.matCategoryTab !== 'none') {
				const n = Number(this.matCategoryTab);
				if (n > 0) categoryId = n;
			}
			return {
				id: 0,
				code: '',
				name: '',
				stock_class: 'finished',
				unit: '个',
				stock_length: '',
				loss_rate: '',
				remark: '',
				goods_id: null,
				category_id: categoryId,
				goods_name: '',
				spec_name: '',
				parent_id: null,
				parent_path: '',
			};
		},
		/** 工具栏：添加物料大项 */
		openMatAdd() {
			this.openMatAddGoods();
		},
		openMatAddGoods() {
			this.matFormMode = 'goods';
			this.matForm = this.blankMatForm();
			this.matDialog = true;
		},
		resetMatFormDialog() {
			this.matFormMode = 'goods';
			this.matForm = this.blankMatForm();
		},
		/** 添加子级规格（任意层级通用；parentItem 为空 = 商品大项下顶级规格） */
		openMatAddChild(group, parentItem) {
			const headerItem = (group.items || []).find((item) => item.is_goods_header);
			const form = this.blankMatForm();
			form.goods_id = group.goods_id;
			form.goods_name = group.goods_name || '';
			form.category_id = group.category_id != null
				? group.category_id
				: (headerItem && headerItem.category_id != null ? headerItem.category_id : null);
			const parent = parentItem || null;
			const parentIsHeader = !!(parent && parent.is_goods_header);
			form.parent_id = parent
				? parent.id
				: (headerItem ? headerItem.id : null);
			// 挂在物料大项下时不算「上级规格」路径，避免重复显示大项名
			form.parent_path = parent && !parentIsHeader
				? this.matSpecPathLabel(group, parent)
				: '';
			// 属性继承：优先父级节点，其次组内任一规格/大项占位行
			const refItem = parent
				|| (group.items || []).find((item) => !item.is_goods_header)
				|| headerItem
				|| null;
			if (refItem) {
				form.stock_class = refItem.stock_class === 'raw' ? 'raw' : 'finished';
				form.unit = refItem.unit || form.unit;
				if (refItem.stock_length != null && refItem.stock_length !== '') {
					form.stock_length = String(refItem.stock_length);
				}
			}
			this.matFormMode = 'node';
			this.matForm = form;
			this.matDialog = true;
		},
		/**
		 * 追加规格（同级）：
		 * - 已有上级规格路径时：在同一父级下新增一条完整组合（如 5mm 下再加 4000K）
		 * - 当前为顶级叶子时：扩展下一维（如 5mm → 5mm/3000K）
		 */
		openMatAppendSpec(group, item) {
			if (!group || !item) return;
			const pid = Number(item.parent_id) || 0;
			const parentItem = (group.items || []).find(
				(it) => it && !it.is_goods_header && Number(it.id) === pid,
			);
			if (parentItem) {
				this.openMatAddChild(group, parentItem);
				return;
			}
			// 顶级叶子：第一次追加 = 向下扩展规格维度
			this.openMatAddChild(group, item);
		},
		openMatEdit(row, group) {
			if (!row || !row.id) return;
			const catId =
				row.category_id != null
					? row.category_id
					: group && group.category_id != null
						? group.category_id
						: null;
			this.matFormMode = 'edit';
			this.matForm = Object.assign(this.blankMatForm(), {
				id: row.id,
				code: row.code || '',
				name: row.name || '',
				stock_class: row.stock_class === 'finished' ? 'finished' : 'raw',
				unit: row.unit || '个',
				stock_length: row.stock_length != null ? String(row.stock_length) : '',
				loss_rate: row.loss_rate != null && row.loss_rate !== '' ? String(row.loss_rate) : '',
				remark: row.remark || '',
				goods_id: row.goods_id || (group && group.goods_id) || null,
				goods_name: (group && group.goods_name) || row.goods_name || '',
				category_id: catId,
			});
			this.matDialog = true;
		},
		/** 与报价购物车名称拆分规则一致 */
		quoteSkuDisplay(goodsName, goodsSkuText) {
			const name = String(goodsName || '').trim();
			const parts = String(goodsSkuText || '')
				.split(',')
				.map((s) => s.trim())
				.filter(Boolean);
			const modelPart = parts[0] || '';
			const parameterText = parts.slice(1).join(' ');
			let display = name;
			if (modelPart) {
				const prefix = name.endsWith('灯条') ? name.slice(0, -2) : name;
				display = prefix + '-' + modelPart;
			}
			return { display_goods_name: display, parameter_text: parameterText };
		},
		/**
		 * 默认用量：
		 * - 堵头配件 → 按灯条数量（一对/一支）
		 * - 型材/罩面/硅胶面 → 按型材数量（切料优化）
		 * - 有原料长 → 按型材数量
		 * - 其余 → 按灯条数量
		 */
		defaultConsumeForMaterial(mat) {
			const name = String((mat && mat.name) || '');
			if (/堵头/.test(name)) return 'fixed';
			if (/型材|罩面|硅胶面/.test(name)) return 'cutting_stock';
			if (mat && Number(mat.stock_length) > 0) return 'cutting_stock';
			return 'fixed';
		},
		isBomWorkspaceGoods(goodsId) {
			const id = Number(goodsId);
			return (this.bomWorkspace || []).some((x) => Number(x.goods_id || x.id) === id);
		},
		bomWorkspaceStorageKey() {
			return 'fsm_bom_workspace_goods_ids_v3';
		},
		bomDataResetFlagKey() {
			return 'fsm_bom_data_reset_v3_goods';
		},
		readBomWorkspaceIds() {
			try {
				const raw = localStorage.getItem(this.bomWorkspaceStorageKey());
				const arr = raw ? JSON.parse(raw) : [];
				return [...new Set((arr || []).map((x) => Number(x)).filter((n) => n > 0))];
			} catch (e) {
				return [];
			}
		},
		persistBomWorkspaceIds(ids) {
			const list = [...new Set((ids || []).map((x) => Number(x)).filter((n) => n > 0))];
			localStorage.setItem(this.bomWorkspaceStorageKey(), JSON.stringify(list));
		},
		syncBomWorkspaceIdsFromState() {
			this.persistBomWorkspaceIds(
				(this.bomWorkspace || []).map((x) => x.goods_id || x.id),
			);
		},
		/** 切换到商品大项级配方：清空旧工作区 key（不批量清库，避免误删） */
		ensureBomDataReset() {
			const that = this;
			try {
				if (localStorage.getItem(that.bomDataResetFlagKey()) === '1') {
					return Promise.resolve();
				}
			} catch (e) { /* ignore */ }
			try {
				localStorage.removeItem('fsm_bom_workspace_ids');
				localStorage.removeItem('fsm_bom_workspace_ids_v2');
				localStorage.setItem(that.bomWorkspaceStorageKey(), JSON.stringify([]));
				localStorage.setItem(that.bomDataResetFlagKey(), '1');
			} catch (e) { /* ignore */ }
			that.bomWorkspace = [];
			return Promise.resolve();
		},
		reloadBomTab() {
			this.loadMatOptions();
			this.loadBomWorkspace();
		},
		bomConsumeTypeText(type) {
			const map = {
				fixed: '按灯条数量',
				length_m: '按订单下单总长度',
				cutting_stock: '按型材数量',
			};
			const key = String(type || 'fixed');
			return map[key] || key || '按灯条数量';
		},
		/** 配方块内短文案，压缩行高 */
		bomConsumeTypeShort(type) {
			const map = {
				fixed: '灯条数',
				length_m: '下单长',
				cutting_stock: '切料',
			};
			const key = String(type || 'fixed');
			return map[key] || this.bomConsumeTypeText(type);
		},
		formatBomFactor(val) {
			const n = Number(val);
			if (!Number.isFinite(n) || n <= 0) return '1';
			return String(n).replace(/\.?0+$/, '') || '1';
		},
		bomLinesSummary(item) {
			const lines = (item && item.lines) || [];
			if (!lines.length) return '';
			return lines
				.map((l) => {
					const name = l.material_name || ('#' + l.material_id);
					const t = this.bomConsumeTypeShort(l.consume_type);
					const f = this.formatBomFactor(l.factor);
					const loss = Number(l.loss_rate) || 0;
					const lossTxt = loss > 0 ? `·损${loss}%` : '';
					return `${name}（${t}×${f}${lossTxt}）`;
				})
				.join('、');
		},
		formatBomLossRate(val) {
			const n = Number(val);
			if (!Number.isFinite(n) || n <= 0) return '—';
			return String(n);
		},
		/** 损耗设定后的折算乘数：10 → ×1.1；0 → ×1 */
		formatBomLossFactor(val) {
			const n = Number(val);
			if (!Number.isFinite(n) || n <= 0) return '×1';
			const mult = 1 + n / 100;
			const text = mult.toFixed(4).replace(/\.?0+$/, '');
			return '×' + text;
		},
		bomLinkedLineCount(item) {
			return (item && item.lines || []).filter((l) => Number(l.material_id) > 0).length;
		},
		bomCommonLines(item) {
			return (item && item.lines || []).filter(
				(l) =>
					!(Number(l.parameter_id) > 0) &&
					!(Number(l.bind_sku_price_id) > 0) &&
					!this.isBomHeaderResolveMode(l.resolve_mode),
			);
		},
		isBomHeaderResolveMode(mode) {
			const m = String(mode || '').trim();
			return m === 'strip_header' || m === 'param_header';
		},
		/** 色温已由「关联灯条大项」匹配，配方块中不再展示 */
		bomRecipeParameterGroups(item) {
			return (item && item.parameter_groups || []).filter(
				(g) => !/色温/.test(String(g.name || '')),
			);
		},
		/** 集成开关等：按物料大项匹配，不再逐选项绑料 */
		isBomParamHeaderGroup(pg) {
			return /集成开关/.test(String((pg && pg.name) || ''));
		},
		blankBomStripLink() {
			return {
				associate: false,
				material_id: 0,
				material_name: '',
				unit: '',
				loss_rate: 0,
				consume_type: 'length_m',
				factor: 1,
			};
		},
		blankBomParamHeaderLink() {
			return {
				material_id: 0,
				material_name: '',
				unit: '',
				loss_rate: 0,
				consume_type: 'fixed',
				factor: 1,
			};
		},
		onBomStripLinkChange(item, on) {
			if (!item) return;
			if (!item.strip_link) this.$set(item, 'strip_link', this.blankBomStripLink());
			item.strip_link.associate = !!on;
			if (!on) {
				item.strip_link.material_id = 0;
				item.strip_link.material_name = '';
				item.strip_link.unit = '';
				item.strip_link.loss_rate = 0;
				item.lines = (item.lines || []).filter(
					(l) => String(l.resolve_mode || '') !== 'strip_header',
				);
			} else {
				this.loadMatHeaderOptions();
			}
			this.markBomDirty(item);
		},
		onBomStripHeaderPick(item) {
			if (!item || !item.strip_link) return;
			const mid = Number(item.strip_link.material_id || 0);
			const hit = (this.matHeaderOptions || []).find((h) => Number(h.id) === mid);
			if (hit) {
				item.strip_link.material_name = hit.name || '';
				item.strip_link.unit = hit.unit || '';
				item.strip_link.loss_rate = Number(hit.loss_rate) || 0;
			} else if (!mid) {
				item.strip_link.material_name = '';
				item.strip_link.unit = '';
				item.strip_link.loss_rate = 0;
			}
			this.syncBomStripLinkLine(item);
		},
		syncBomStripLinkLine(item, opts) {
			if (!item) return;
			if (!item.strip_link) this.$set(item, 'strip_link', this.blankBomStripLink());
			const link = item.strip_link;
			const mid = Number(link.material_id || 0);
			let lines = (item.lines || []).filter(
				(l) => String(l.resolve_mode || '') !== 'strip_header',
			);
			if (link.associate && mid > 0) {
				lines.push({
					material_id: mid,
					material_name: link.material_name || '',
					unit: link.unit || '',
					stock_class: 'raw',
					loss_rate: Number(link.loss_rate) || 0,
					consume_type: link.consume_type || 'length_m',
					factor: link.factor != null ? link.factor : 1,
					parameter_id: 0,
					parameter_pid: 0,
					bind_sku_price_id: 0,
					resolve_mode: 'strip_header',
				});
			}
			item.lines = lines;
			if (!(opts && opts.silent)) this.markBomDirty(item);
		},
		onBomParamHeaderPick(item, pg) {
			if (!item || !pg) return;
			if (!pg.header_link) this.$set(pg, 'header_link', this.blankBomParamHeaderLink());
			const mid = Number(pg.header_link.material_id || 0);
			const hit = (this.matHeaderOptions || []).find((h) => Number(h.id) === mid);
			if (hit) {
				pg.header_link.material_name = hit.name || '';
				pg.header_link.unit = hit.unit || '';
				pg.header_link.loss_rate = Number(hit.loss_rate) || 0;
			} else if (!mid) {
				pg.header_link.material_name = '';
				pg.header_link.unit = '';
				pg.header_link.loss_rate = 0;
			}
			this.syncBomParamHeaderLink(item, pg);
		},
		syncBomParamHeaderLink(item, pg, opts) {
			if (!item || !pg) return;
			if (!pg.header_link) this.$set(pg, 'header_link', this.blankBomParamHeaderLink());
			const pid = Number(pg.id) || 0;
			const link = pg.header_link;
			const mid = Number(link.material_id || 0);
			let lines = (item.lines || []).filter(
				(l) =>
					!(
						String(l.resolve_mode || '') === 'param_header' &&
						Number(l.parameter_pid) === pid
					) &&
					!(Number(l.parameter_pid) === pid && Number(l.parameter_id) > 0),
			);
			if (pg.associate && mid > 0 && pid > 0) {
				lines.push({
					material_id: mid,
					material_name: link.material_name || '',
					unit: link.unit || '',
					stock_class: 'raw',
					loss_rate: Number(link.loss_rate) || 0,
					consume_type: link.consume_type || 'fixed',
					factor: link.factor != null ? link.factor : 1,
					parameter_id: 0,
					parameter_pid: pid,
					bind_sku_price_id: 0,
					resolve_mode: 'param_header',
				});
			}
			item.lines = lines;
			if (!(opts && opts.silent)) this.markBomDirty(item);
		},
		preferBomParamHeaderMaterial(pg) {
			const name = String((pg && pg.name) || '');
			const opts = this.matHeaderOptions || [];
			if (/集成开关/.test(name)) {
				const hit = opts.find((h) => String(h.name || '') === '普通开关');
				if (hit) return hit;
			}
			return null;
		},
		bomParamOptionRows(item, pg) {
			const lines = (item && item.lines) || [];
			return (pg.children || []).map((child) => {
				const hit = lines.find((l) => Number(l.parameter_id) === Number(child.id));
				const defaultType = 'length_m';
				return {
					option_id: Number(child.id),
					option_name: child.name || ('#' + child.id),
					material_id: hit ? Number(hit.material_id) : 0,
					material_name: hit ? (hit.material_name || '') : '',
					unit: hit ? (hit.unit || '') : '',
					stock_class: hit ? (hit.stock_class || 'raw') : 'raw',
					loss_rate: hit ? (Number(hit.loss_rate) || 0) : 0,
					consume_type: hit ? (hit.consume_type || defaultType) : defaultType,
					factor: hit && hit.factor != null ? hit.factor : 1,
					parameter_id: Number(child.id),
					parameter_pid: Number(pg.id),
				};
			});
		},
		/**
		 * 规格组合：左→右维度列 + 纵向合并；同一组合可多物料行
		 */
		bomSkuComboTable(item) {
			const lines = (item && item.lines) || [];
			const comboSrc = (item && item.sku_combos) || {};
			const list = comboSrc.list || [];
			const dimNames = (comboSrc.dimensions || [])
				.map((d) => (d && (d.name || d)) || '')
				.filter(Boolean);
			const maxDepth = list.reduce((m, c) => {
				const n = (c.spec_values && c.spec_values.length) || 0;
				return Math.max(m, n);
			}, 0);
			const dimCount = Math.max(dimNames.length, maxDepth, 1);
			const dimensions = Array.from({ length: dimCount }, (_, i) => dimNames[i] || `规格${i + 1}`);
			const rows = [];
			list.forEach((combo) => {
				const bindId = Number(combo.id);
				const hits = lines.filter((l) => Number(l.bind_sku_price_id) === bindId);
				const defaultType = 'cutting_stock';
				const rawVals = Array.isArray(combo.spec_values) ? combo.spec_values.slice() : [];
				if (!rawVals.length && combo.label) {
					String(combo.label)
						.split(/\s*[\/|,，]\s*/)
						.forEach((s) => {
							const t = String(s || '').trim();
							if (t) rawVals.push(t);
						});
				}
				const specValues = Array.from({ length: dimCount }, (_, i) => String(rawVals[i] || ''));
				const optionName = combo.label || specValues.filter(Boolean).join(' / ') || ('#' + bindId);
				const modelSkuId = Number(combo.model_sku_id) || 0;
				const stripWidth = String(combo.strip_width || '').trim();
				const pushRow = (hit, mi) => {
					rows.push({
						rowKey: 'bom-sku-' + bindId + '-' + (hit ? hit.material_id : 0) + '-' + mi,
						bind_sku_price_id: bindId,
						option_name: optionName,
						model_sku_id: modelSkuId,
						strip_width: stripWidth,
						specValues,
						specShow: Array.from({ length: dimCount }, () => true),
						specRowspan: Array.from({ length: dimCount }, () => 1),
						showStripWidth: true,
						stripWidthRowspan: 1,
						material_id: hit ? Number(hit.material_id) : 0,
						material_name: hit ? (hit.material_name || '') : '',
						unit: hit ? (hit.unit || '') : '',
						stock_class: hit ? (hit.stock_class || 'raw') : 'raw',
						loss_rate: hit ? (Number(hit.loss_rate) || 0) : 0,
						consume_type: hit ? (hit.consume_type || defaultType) : defaultType,
						factor: hit && hit.factor != null ? hit.factor : 1,
					});
				};
				if (hits.length) hits.forEach((h, mi) => pushRow(h, mi));
				else pushRow(null, 0);
			});
			this.applyMatSpecTreeMerge(rows, dimCount);
			// 灯条宽度列按「型号」合并（与第 0 列 rowspan 对齐）
			rows.forEach((r) => {
				r.showStripWidth = !!r.specShow[0];
				r.stripWidthRowspan = Number(r.specRowspan[0]) || 1;
			});
			return { dimensions, rows };
		},
		onBomModelStripWidthChange(item, row, value) {
			if (!item || !row) return;
			const modelId = Number(row.model_sku_id) || 0;
			const width = String(value || '').trim();
			const list = (item.sku_combos && item.sku_combos.list) || [];
			if (modelId > 0) {
				list.forEach((c) => {
					if (Number(c.model_sku_id) === modelId) {
						this.$set(c, 'strip_width', width);
					}
				});
			} else {
				const bindId = Number(row.bind_sku_price_id) || 0;
				const hit = list.find((c) => Number(c.id) === bindId);
				if (hit) this.$set(hit, 'strip_width', width);
			}
			this.markBomDirty(item);
		},
		collectBomModelStripWidths(item) {
			const list = (item && item.sku_combos && item.sku_combos.list) || [];
			const map = new Map();
			list.forEach((c) => {
				const mid = Number(c.model_sku_id) || 0;
				if (!(mid > 0)) return;
				if (!map.has(mid)) {
					map.set(mid, {
						model_sku_id: mid,
						strip_width: String(c.strip_width || '').trim(),
					});
				}
			});
			return [...map.values()];
		},
		onBomParamAssociateChange(item, pg, on) {
			if (!item || !pg) return;
			pg.associate = !!on;
			if (this.isBomParamHeaderGroup(pg)) {
				if (!pg.header_link) this.$set(pg, 'header_link', this.blankBomParamHeaderLink());
				if (!on) {
					pg.header_link.material_id = 0;
					pg.header_link.material_name = '';
					pg.header_link.unit = '';
					pg.header_link.loss_rate = 0;
					this.syncBomParamHeaderLink(item, pg);
				} else {
					this.loadMatHeaderOptions().then(() => {
						if (!(Number(pg.header_link.material_id) > 0)) {
							const prefer = this.preferBomParamHeaderMaterial(pg);
							if (prefer) {
								pg.header_link.material_id = Number(prefer.id);
								pg.header_link.material_name = prefer.name || '';
								pg.header_link.unit = prefer.unit || '';
								pg.header_link.loss_rate = Number(prefer.loss_rate) || 0;
							}
						}
						this.syncBomParamHeaderLink(item, pg);
					}).catch(() => {
						this.syncBomParamHeaderLink(item, pg);
					});
				}
				return;
			}
			if (!on) {
				const pid = Number(pg.id);
				item.lines = (item.lines || []).filter((l) => Number(l.parameter_pid) !== pid);
			}
			this.markBomDirty(item);
		},
		onBomSkuComboAssociateChange(item, on) {
			if (!item) return;
			if (!item.sku_combos) this.$set(item, 'sku_combos', { associate: false, list: [] });
			item.sku_combos.associate = !!on;
			if (!on) {
				item.lines = (item.lines || []).filter((l) => !(Number(l.bind_sku_price_id) > 0));
			}
			this.markBomDirty(item);
		},
		syncBomParamOptionLine(item, pg, row) {
			if (!item || !pg || !row || !(Number(row.material_id) > 0)) return;
			const optId = Number(row.option_id || row.parameter_id);
			const list = item.lines || [];
			const idx = list.findIndex((l) => Number(l.parameter_id) === optId);
			const payload = {
				material_id: Number(row.material_id),
				material_name: row.material_name || '',
				unit: row.unit || '',
				stock_class: row.stock_class || 'raw',
				loss_rate: Number(row.loss_rate) || 0,
				consume_type: row.consume_type || 'length_m',
				factor: row.factor != null ? row.factor : 1,
				parameter_id: optId,
				parameter_pid: Number(pg.id),
				bind_sku_price_id: 0,
			};
			if (idx >= 0) Object.assign(list[idx], payload);
			else list.push(payload);
			item.lines = list;
			this.markBomDirty(item);
		},
		syncBomSkuComboLine(item, row) {
			if (!item || !row || !(Number(row.material_id) > 0)) return;
			const bindId = Number(row.bind_sku_price_id);
			const mid = Number(row.material_id);
			if (!(bindId > 0) || !(mid > 0)) return;
			const list = item.lines || [];
			const idx = list.findIndex(
				(l) =>
					Number(l.bind_sku_price_id) === bindId &&
					Number(l.material_id) === mid,
			);
			const payload = {
				material_id: mid,
				material_name: row.material_name || '',
				unit: row.unit || '',
				stock_class: row.stock_class || 'raw',
				loss_rate: Number(row.loss_rate) || 0,
				consume_type: row.consume_type || 'cutting_stock',
				factor: row.factor != null ? row.factor : 1,
				parameter_id: 0,
				parameter_pid: 0,
				bind_sku_price_id: bindId,
			};
			if (idx >= 0) Object.assign(list[idx], payload);
			else list.push(payload);
			item.lines = list;
			if (item.sku_combos) item.sku_combos.associate = true;
			this.markBomDirty(item);
		},
		removeBomSkuComboMaterial(item, row) {
			if (!item || !row) return;
			const bindId = Number(row.bind_sku_price_id) || 0;
			const mid = Number(row.material_id) || 0;
			if (!(bindId > 0) || !(mid > 0)) return;
			item.lines = (item.lines || []).filter(
				(l) =>
					!(
						Number(l.bind_sku_price_id) === bindId &&
						Number(l.material_id) === mid
					),
			);
			this.markBomDirty(item);
		},
		mapBomWorkspaceRow(row) {
			const goodsId = Number(row.goods_id || row.id) || 0;
			const lines = (row.lines || []).map((l) => ({
				material_id: l.material_id,
				material_name: l.material_name || '',
				unit: l.unit || '',
				stock_class: l.stock_class || 'raw',
				loss_rate: Number(l.loss_rate) || 0,
				consume_type: l.consume_type || 'fixed',
				factor: l.factor != null ? l.factor : 1,
				parameter_id: Number(l.parameter_id) || 0,
				parameter_pid: Number(l.parameter_pid) || 0,
				bind_sku_price_id: Number(l.bind_sku_price_id) || 0,
				resolve_mode: String(l.resolve_mode || '').trim(),
			}));
			const linkedParam = {};
			const paramHeaderByPid = {};
			let hasComboBind = false;
			let stripLine = null;
			lines.forEach((l) => {
				const mode = String(l.resolve_mode || '').trim();
				if (mode === 'param_header' && Number(l.parameter_pid) > 0) {
					paramHeaderByPid[Number(l.parameter_pid)] = l;
					linkedParam[Number(l.parameter_pid)] = true;
				} else if (Number(l.parameter_pid) > 0 && Number(l.parameter_id) > 0) {
					linkedParam[Number(l.parameter_pid)] = true;
				}
				if (Number(l.bind_sku_price_id) > 0) hasComboBind = true;
				if (mode === 'strip_header' && !stripLine) stripLine = l;
			});
			const parameterGroups = (row.parameter_groups || []).map((g) => {
				const gid = Number(g.id);
				const isHeaderMode = /集成开关/.test(String(g.name || ''));
				const headerLine = paramHeaderByPid[gid];
				const header_link = this.blankBomParamHeaderLink();
				if (headerLine) {
					header_link.material_id = Number(headerLine.material_id) || 0;
					header_link.material_name = headerLine.material_name || '';
					header_link.unit = headerLine.unit || '';
					header_link.loss_rate = Number(headerLine.loss_rate) || 0;
					header_link.consume_type = headerLine.consume_type || 'fixed';
					header_link.factor = headerLine.factor != null ? headerLine.factor : 1;
				}
				return {
					id: gid,
					name: g.name || '',
					must: !!g.must,
					associate: isHeaderMode
						? !!headerLine
						: (g.associate != null ? !!g.associate : !!linkedParam[gid]),
					header_link,
					children: (g.children || []).map((c) => ({
						id: Number(c.id),
						name: c.name || '',
					})),
				};
			});
			const comboSrc = row.sku_combos || {};
			const skuCombos = {
				associate:
					comboSrc.associate != null ? !!comboSrc.associate : hasComboBind,
				dimensions: (comboSrc.dimensions || []).map((d) => ({
					id: Number(d.id) || 0,
					name: d.name || '',
				})),
				list: (comboSrc.list || []).map((c) => ({
					id: Number(c.id),
					label: c.label || '',
					stock_length: Number(c.stock_length) || 0,
					goods_sku_ids: c.goods_sku_ids || '',
					model_sku_id: Number(c.model_sku_id) || 0,
					strip_width: String(c.strip_width || '').trim(),
					spec_values: Array.isArray(c.spec_values)
						? c.spec_values.map((v) => String(v || ''))
						: [],
				})),
			};
			const strip_link = this.blankBomStripLink();
			if (stripLine) {
				strip_link.associate = true;
				strip_link.material_id = Number(stripLine.material_id) || 0;
				strip_link.material_name = stripLine.material_name || '';
				strip_link.unit = stripLine.unit || '';
				strip_link.loss_rate = Number(stripLine.loss_rate) || 0;
				strip_link.consume_type = stripLine.consume_type || 'length_m';
				strip_link.factor = stripLine.factor != null ? stripLine.factor : 1;
			}
			return {
				id: goodsId,
				goods_id: goodsId,
				goods_name: row.goods_name || '',
				display_goods_name: row.goods_name || '',
				parameter_text: '',
				summary: row.summary || '',
				add_size: !!row.add_size,
				lines,
				parameter_groups: parameterGroups,
				sku_combos: skuCombos,
				strip_link,
				dirty: false,
				saving: false,
			};
		},
		loadBomWorkspace() {
			const that = this;
			const localIds = that.readBomWorkspaceIds();
			const dirtyMap = {};
			(that.bomWorkspace || []).forEach((x) => {
				if (x.dirty) dirtyMap[Number(x.goods_id || x.id)] = x;
			});
			if (!localIds.length) {
				that.bomWorkspace = Object.keys(dirtyMap).map((k) => dirtyMap[k]);
				return;
			}
			that.bomWorkspaceLoading = true;
			that.$post('addons/hlm/api.FactoryStock/bomWorkspaceList', {
				goods_ids: localIds.join(','),
			})
				.then((res) => {
					if (res.code != 1) return;
					const list = ((res.data && res.data.list) || []).map((row) => that.mapBomWorkspaceRow(row));
					const byId = {};
					list.forEach((row) => { byId[row.goods_id] = dirtyMap[row.goods_id] || row; });
					Object.keys(dirtyMap).forEach((k) => {
						const id = Number(k);
						if (!byId[id]) byId[id] = dirtyMap[id];
					});
					const ordered = localIds.map((id) => byId[id]).filter(Boolean);
					const known = {};
					ordered.forEach((row) => { known[row.goods_id] = true; });
					Object.keys(dirtyMap).forEach((k) => {
						const id = Number(k);
						if (!known[id]) ordered.push(dirtyMap[id]);
					});
					that.bomWorkspace = ordered;
					that.syncBomWorkspaceIdsFromState();
				})
				.finally(() => {
					that.bomWorkspaceLoading = false;
				});
		},
		openBomSkuPick() {
			if (!this.activeRootId) {
				this.$message.warning('请先选择产品大类');
				return;
			}
			this.bomPickSearch = '';
			this.bomPickTypeIndex = 0;
			this.bomPickSelectedGood = null;
			this.bomPickVisible = true;
		},
		onBomPickOpen() {
			this.bomPickSelectedGood = null;
			this.loadBomPickTypes().then(() => this.loadBomPickGoods());
		},
		loadBomPickTypes() {
			const that = this;
			const pid = that.activeRootId;
			const base = [{ id: '', name: '全部分类' }];
			if (!pid) {
				that.bomPickTypes = base;
				that.bomPickTypeIndex = 0;
				return Promise.resolve();
			}
			return that
				.$fetch('addons/hlm/api.Goods/getCategory', { pid })
				.then((res) => {
					if (res.code == 1) {
						that.bomPickTypes = base.concat(res.data || []);
					} else {
						that.bomPickTypes = base;
					}
					that.bomPickTypeIndex = 0;
				})
				.catch(() => {
					that.bomPickTypes = base;
					that.bomPickTypeIndex = 0;
				});
		},
		searchBomGood() {
			this.bomPickGoodList = [];
			this.loadBomPickGoods();
		},
		changeBomGoodType() {
			this.bomPickGoodList = [];
			this.loadBomPickGoods();
		},
		loadBomPickGoods() {
			const that = this;
			const pid = that.activeRootId;
			if (!pid) {
				that.bomPickGoodList = [];
				return;
			}
			const type = that.bomPickTypes[that.bomPickTypeIndex] || { id: '' };
			that.bomPickLoading = true;
			that
				.$fetch('addons/hlm/api.Goods/getGoodsList', {
					page: 1,
					limit: 200,
					pid,
					search: that.bomPickSearch,
					category_id: type.id,
				})
				.then((res) => {
					if (res.code != 1) return;
					that.bomPickGoodList = (res.data && res.data.list) || [];
				})
				.finally(() => {
					that.bomPickLoading = false;
				});
		},
		selectBomGood(row) {
			this.bomPickSelectedGood = row || null;
		},
		chooseBomGood(row) {
			const that = this;
			const goods = row || that.bomPickSelectedGood;
			if (!goods || !goods.id) {
				that.$message.warning('请选择需要添加的产品');
				return;
			}
			const goodsId = Number(goods.id);
			if (that.isBomWorkspaceGoods(goodsId)) {
				that.$message.warning('该产品大项已在配方列表中');
				return;
			}
			that.bomTplPendingGood = goods;
			that.bomPickVisible = false;
			that.bomTplVisible = true;
			that.bomTplCode = 'strip_process';
			that.loadBomTemplateList();
		},
		loadBomTemplateList() {
			const that = this;
			that.bomTplLoading = true;
			that
				.$fetch('addons/hlm/api.FactoryStock/bomTemplateList', {
					pid: that.activeRootId || 0,
				})
				.then((res) => {
					if (res.code != 1) return;
					that.bomTplList = (res.data && res.data.list) || [];
					const prefer = that.bomTplList.find(
						(t) => t.code === 'strip_process' && t.available,
					);
					const blank = that.bomTplList.find((t) => t.code === 'blank');
					that.bomTplCode = prefer
						? prefer.code
						: blank
							? blank.code
							: (that.bomTplList[0] && that.bomTplList[0].code) || 'blank';
				})
				.finally(() => {
					that.bomTplLoading = false;
				});
		},
		cancelBomTemplatePick() {
			this.bomTplVisible = false;
			this.bomTplPendingGood = null;
			this.bomTplSaving = false;
		},
		confirmBomTemplatePick() {
			const that = this;
			const goods = that.bomTplPendingGood;
			if (!goods || !goods.id) {
				that.$message.warning('请先选择产品');
				return;
			}
			const goodsId = Number(goods.id);
			if (that.isBomWorkspaceGoods(goodsId)) {
				that.$message.warning('该产品大项已在配方列表中');
				return;
			}
			const code = that.bomTplCode || 'blank';
			const tpl = (that.bomTplList || []).find((t) => t.code === code);
			if (tpl && code !== 'blank' && !tpl.available) {
				that.$message.warning('该模板源配方尚未配置，请先完善「嵌入式灯条」或选空白配方');
				return;
			}
			that.bomTplSaving = true;
			that
				.$post('addons/hlm/api.FactoryStock/bomTemplateApply', {
					goods_id: goodsId,
					template_code: code,
					source_goods_id: tpl && tpl.source_goods_id ? tpl.source_goods_id : 0,
				})
				.then((res) => {
					if (res.code != 1 || !res.data || !res.data.goods) {
						that.$message.warning((res && res.msg) || '套用模板失败');
						return;
					}
					const mapped = that.mapBomWorkspaceRow({
						...res.data.goods,
						lines: res.data.lines || [],
						parameter_groups: res.data.parameter_groups || [],
						sku_combos: res.data.sku_combos || { associate: false, list: [] },
					});
					const applied = Number(
						(res.data.template && res.data.template.applied_lines) || 0,
					);
					if (!that.isBomWorkspaceGoods(mapped.goods_id)) {
						that.bomWorkspace.push(mapped);
					}
					that.syncBomWorkspaceIdsFromState();
					that.bomTplVisible = false;
					that.bomTplPendingGood = null;
					const tplName =
						(res.data.template && res.data.template.name) || '模板';
					that.$message.success(
						code === 'blank'
							? `已加入「${mapped.goods_name}」`
							: `已加入「${mapped.goods_name}」并套用「${tplName}」${applied ? `（${applied} 行）` : ''}`,
					);
				})
				.finally(() => {
					that.bomTplSaving = false;
				});
		},
		openBomMatDialog(item, ctx) {
			if (!this.activeRootId) {
				this.$message.warning('请先选择产品大类');
				return;
			}
			if (!item || !(item.goods_id || item.id)) {
				this.$message.warning('请先添加加工产品，再关联物料');
				return;
			}
			this.bomMatTargetId = Number(item.goods_id || item.id);
			if (ctx && ctx.mode === 'param') {
				this.bomMatTargetCtx = {
					mode: 'param',
					parameter_pid: Number(ctx.parameter_pid) || 0,
					parameter_id: Number(ctx.parameter_id) || 0,
					option_name: ctx.option_name || '',
				};
			} else if (ctx && ctx.mode === 'sku_combo') {
				this.bomMatTargetCtx = {
					mode: 'sku_combo',
					bind_sku_price_id: Number(ctx.bind_sku_price_id) || 0,
					option_name: ctx.option_name || '',
					replace_material_id: Number(ctx.replace_material_id) || 0,
				};
			} else {
				this.bomMatTargetCtx = { mode: 'common' };
			}
			this.bomMatDialogVisible = true;
		},
		onBomMatDialogOpen() {
			this.loadMatOptions();
		},
		bomMatRowClassName({ row }) {
			return this.bomMatTargetHas(row && row.id) ? 'is-added-row' : '';
		},
		bomMatTargetItem() {
			const id = Number(this.bomMatTargetId) || 0;
			if (!id) return null;
			return (this.bomWorkspace || []).find(
				(x) => Number(x.goods_id || x.id) === id,
			) || null;
		},
		bomMatTargetHas(materialId) {
			const item = this.bomMatTargetItem();
			if (!item) return false;
			const mid = Number(materialId);
			const ctx = this.bomMatTargetCtx || { mode: 'common' };
			if (ctx.mode === 'param') {
				const optId = Number(ctx.parameter_id) || 0;
				return (item.lines || []).some(
					(l) => Number(l.parameter_id) === optId && Number(l.material_id) === mid,
				);
			}
			if (ctx.mode === 'sku_combo') {
				const bindId = Number(ctx.bind_sku_price_id) || 0;
				return (item.lines || []).some(
					(l) => Number(l.bind_sku_price_id) === bindId && Number(l.material_id) === mid,
				);
			}
			return (item.lines || []).some(
				(l) =>
					!(Number(l.parameter_id) > 0) &&
					!(Number(l.bind_sku_price_id) > 0) &&
					Number(l.material_id) === mid,
			);
		},
		addBomMatFromDialog(mat) {
			const item = this.bomMatTargetItem();
			if (!item) {
				this.$message.warning('请先选择要关联的加工产品');
				return;
			}
			if (!mat) return;
			const mid = Number(mat.id);
			const ctx = this.bomMatTargetCtx || { mode: 'common' };
			const base = {
				material_id: mid,
				material_name: mat.name,
				unit: mat.unit || '',
				stock_class: mat.stock_class || 'raw',
				loss_rate: Number(mat.loss_rate) || 0,
				consume_type: this.defaultConsumeForMaterial(mat),
				factor: 1,
				parameter_id: 0,
				parameter_pid: 0,
				bind_sku_price_id: 0,
			};
			if (ctx.mode === 'param') {
				const optId = Number(ctx.parameter_id) || 0;
				const groupId = Number(ctx.parameter_pid) || 0;
				if (!(optId > 0) || !(groupId > 0)) {
					this.$message.warning('参数选项无效');
					return;
				}
				base.parameter_id = optId;
				base.parameter_pid = groupId;
				base.consume_type = this.defaultConsumeForMaterial(mat);
				const idx = (item.lines || []).findIndex((l) => Number(l.parameter_id) === optId);
				if (idx >= 0) Object.assign(item.lines[idx], base);
				else item.lines.push(base);
				const pg = (item.parameter_groups || []).find((g) => Number(g.id) === groupId);
				if (pg) pg.associate = true;
				this.markBomDirty(item);
				this.$message.success(`已关联「${ctx.option_name || mat.name}」`);
				this.bomMatDialogVisible = false;
				return;
			}
			if (ctx.mode === 'sku_combo') {
				const bindId = Number(ctx.bind_sku_price_id) || 0;
				if (!(bindId > 0)) {
					this.$message.warning('规格组合无效');
					return;
				}
				base.bind_sku_price_id = bindId;
				base.consume_type = this.defaultConsumeForMaterial(mat);
				const replaceId = Number(ctx.replace_material_id) || 0;
				const list = item.lines || [];
				if (replaceId > 0) {
					const idx = list.findIndex(
						(l) =>
							Number(l.bind_sku_price_id) === bindId &&
							Number(l.material_id) === replaceId,
					);
					if (idx >= 0) Object.assign(list[idx], base);
					else list.push(base);
				} else if (
					list.some(
						(l) =>
							Number(l.bind_sku_price_id) === bindId &&
							Number(l.material_id) === mid,
					)
				) {
					this.$message.warning('该物料已在此规格组合中');
					return;
				} else {
					list.push(base);
				}
				item.lines = list;
				if (!item.sku_combos) this.$set(item, 'sku_combos', { associate: true, list: [] });
				item.sku_combos.associate = true;
				this.markBomDirty(item);
				this.$message.success(
					replaceId > 0
						? `已更换「${ctx.option_name || mat.name}」物料`
						: `已关联「${ctx.option_name || mat.name}」`,
				);
				this.bomMatDialogVisible = false;
				return;
			}
			if (
				(item.lines || []).some(
					(l) =>
						!(Number(l.parameter_id) > 0) &&
						!(Number(l.bind_sku_price_id) > 0) &&
						Number(l.material_id) === mid,
				)
			) {
				this.$message.warning('该物料已在公共物料中');
				return;
			}
			item.lines.push(base);
			this.markBomDirty(item);
			this.$message.success(`已加入公共物料「${mat.name}」`);
		},
		markBomDirty(item) {
			if (!item) return;
			item.dirty = true;
			this.scheduleBomAutoSave(item);
		},
		scheduleBomAutoSave(item) {
			const goodsId = Number(item && (item.goods_id || item.id));
			if (!(goodsId > 0)) return;
			if (!this._bomSaveTimers) this._bomSaveTimers = {};
			clearTimeout(this._bomSaveTimers[goodsId]);
			this._bomSaveTimers[goodsId] = setTimeout(() => {
				delete this._bomSaveTimers[goodsId];
				this.saveBomWorkspaceItem(item, { quiet: true });
			}, 280);
		},
		removeBomLineRow(item, row) {
			if (!item || !row) return;
			const mid = Number(row.material_id);
			const paramId = Number(row.parameter_id) || 0;
			const bindSku = Number(row.bind_sku_price_id) || 0;
			item.lines = (item.lines || []).filter((l) => {
				const same =
					Number(l.material_id) === mid &&
					(Number(l.parameter_id) || 0) === paramId &&
					(Number(l.bind_sku_price_id) || 0) === bindSku;
				return !same;
			});
			this.markBomDirty(item);
		},
		removeBomWorkspaceItem(item) {
			const that = this;
			const goodsId = Number(item.goods_id || item.id);
			if (that._bomSaveTimers && that._bomSaveTimers[goodsId]) {
				clearTimeout(that._bomSaveTimers[goodsId]);
				delete that._bomSaveTimers[goodsId];
			}
			that.$confirm('将移出列表并删除该产品大项已保存的配方，确认？', '提示', { type: 'warning' })
				.then(() => {
					that.$post('addons/hlm/api.FactoryStock/bomClear', {
						goods_id: goodsId,
					}).then((res) => {
						if (res.code == 1) {
							that.bomWorkspace = that.bomWorkspace.filter(
								(x) => Number(x.goods_id || x.id) !== goodsId,
							);
							that.syncBomWorkspaceIdsFromState();
							that.$message.success(res.msg || '已移除');
						}
					});
				})
				.catch(() => {});
		},
		saveBomWorkspaceItem(item, opts) {
			const that = this;
			const quiet = !!(opts && opts.quiet);
			const goodsId = Number(item && (item.goods_id || item.id));
			if (!(goodsId > 0)) return;
			if (item.saving) {
				if (!that._bomSaveAgain) that._bomSaveAgain = {};
				that._bomSaveAgain[goodsId] = true;
				return;
			}
			that.syncBomStripLinkLine(item, { silent: true });
			(that.bomRecipeParameterGroups(item) || []).forEach((pg) => {
				if (that.isBomParamHeaderGroup(pg)) {
					that.syncBomParamHeaderLink(item, pg, { silent: true });
				}
			});
			const lines = (item.lines || [])
				.filter((l) => Number(l.material_id) > 0)
				.map((l) => ({
					material_id: l.material_id,
					consume_type: l.consume_type,
					factor: l.factor,
					parameter_id: Number(l.parameter_id) || 0,
					parameter_pid: Number(l.parameter_pid) || 0,
					bind_sku_price_id: Number(l.bind_sku_price_id) || 0,
					resolve_mode: String(l.resolve_mode || '').trim(),
				}));
			if (item.add_size && !lines.length) {
				that.$message.warning('加工型产品请至少关联一行物料');
				return;
			}
			that.$set(item, 'saving', true);
			that.$post('addons/hlm/api.FactoryStock/bomSave', {
				goods_id: goodsId,
				lines: JSON.stringify(lines),
				model_strip_widths: JSON.stringify(that.collectBomModelStripWidths(item)),
			})
				.then((res) => {
					if (res.code == 1) {
						if (!quiet) that.$message.success(res.msg || '配方已保存');
						item.dirty = false;
						that.syncBomWorkspaceIdsFromState();
					}
				})
				.finally(() => {
					that.$set(item, 'saving', false);
					if (that._bomSaveAgain && that._bomSaveAgain[goodsId]) {
						delete that._bomSaveAgain[goodsId];
						that.saveBomWorkspaceItem(item, { quiet: true });
					}
				});
		},
		saveMat() {
			const that = this;
			const isEdit = that.matFormMode === 'edit';
			const isGoodsOnly = that.matFormMode === 'goods';
			const isNode = that.matFormMode === 'node';
			if (isGoodsOnly) {
				const goodsName = String(that.matForm.goods_name || '').trim();
				if (!goodsName) {
					that.$message.warning('请填写物料大项名称');
					return;
				}
				that.matForm.goods_name = goodsName;
			} else if (isNode) {
				if (!String(that.matForm.goods_name || '').trim()) {
					that.$message.warning('商品大项无效');
					return;
				}
				const seg = String(that.matForm.spec_name || '').trim();
				if (!seg) {
					that.$message.warning('请填写规格名称');
					return;
				}
				that.matForm.spec_name = seg;
			} else if (!String(that.matForm.name || '').trim()) {
				that.$message.warning('请填写名称');
				return;
			}
			const payload = {
				id: that.matForm.id || 0,
				code: String(that.matForm.code || '').trim(),
				stock_class: that.matForm.stock_class || 'finished',
				unit: that.matForm.unit,
				stock_length: that.matForm.stock_length,
				remark: that.matForm.remark,
			};
			if (isEdit || isGoodsOnly) {
				const lrRaw = String(that.matForm.loss_rate ?? '').trim();
				payload.loss_rate = lrRaw === '' ? 0 : lrRaw;
			}
			if (isEdit) {
				payload.name = that.matForm.name;
				payload.goods_id = that.matForm.goods_id;
				payload.goods_name = String(that.matForm.goods_name || '').trim();
				payload.category_id = that.matForm.category_id;
				payload.sync_group_category = 1;
			} else if (isGoodsOnly) {
				payload.save_goods_only = 1;
				payload.goods_name = String(that.matForm.goods_name || '').trim();
				payload.category_id = that.matForm.category_id;
				payload.code = String(that.matForm.code || '').trim();
				payload.stock_class = that.matForm.stock_class || 'finished';
				payload.unit = that.matForm.unit || '个';
				payload.stock_length = that.matForm.stock_length;
			} else if (isNode) {
				payload.save_spec_node = 1;
				payload.goods_id = that.matForm.goods_id || null;
				payload.goods_name = String(that.matForm.goods_name || '').trim();
				payload.parent_id = that.matForm.parent_id || null;
				payload.spec_name = String(that.matForm.spec_name || '').trim();
			}
			that.matSaving = true;
			that.$post('addons/hlm/api.FactoryStock/materialSave', payload)
				.then((res) => {
					if (res.code == 1) {
						that.$message.success(res.msg || '保存成功');
						that.matDialog = false;
						that.loadMaterials();
						that.loadMatOptions();
						if (that.tab === 'bom') that.loadBomWorkspace();
					}
				})
				.finally(() => {
					that.matSaving = false;
				});
		},
		/** 收集节点及其全部后代 id（无限级子树删除） */
		collectMatSubtreeIds(group, itemId) {
			const id = Number(itemId) || 0;
			if (!id) return [];
			const items = ((group && group.items) || []).filter((it) => !it.is_goods_header);
			const childMap = {};
			items.forEach((it) => {
				const pid = Number(it.parent_id) || 0;
				if (!childMap[pid]) childMap[pid] = [];
				childMap[pid].push(it.id);
			});
			const out = [];
			const stack = [id];
			while (stack.length) {
				const cur = stack.pop();
				out.push(cur);
				(childMap[cur] || []).forEach((cid) => stack.push(cid));
			}
			return out;
		},
		/** 删除规格节点（含其子树） */
		delSpecNode(group, item) {
			const that = this;
			if (!item || !item.id) return;
			const ids = that.collectMatSubtreeIds(group, item.id);
			const childCount = ids.length - 1;
			that.$confirm(
				`将删除规格「${item.name}」${childCount > 0 ? `及其下 ${childCount} 条子级规格` : ''}，是否继续？`,
				'删除规格',
				{ type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' },
			).then(() => {
				that.$fetch('addons/hlm/api.FactoryStock/materialDel', { id: ids.join(',') }).then((res) => {
					if (res.code == 1) {
						that.$message.success('已删除');
						that.loadMaterials();
						that.loadMatOptions();
					}
				});
			}).catch(() => {});
		},
		delGoodsGroup(group) {
			const that = this;
			const name = (group && group.goods_name) || '该商品';
			const specCount = (group && group.item_count) || ((group && group.items || []).filter((item) => !item.is_goods_header).length) || 0;
			const count = specCount || ((group && group.items && group.items.length) || 0);
			const ids = ((group && group.items) || []).map((item) => item.id).filter((id) => id > 0);
			if (!ids.length) {
				that.$message.warning('该商品大项下暂无物料');
				return;
			}
			that.$confirm(
				`将删除「${name}」${specCount ? `下全部 ${specCount} 条规格` : ''}及其配方，是否继续？`,
				'删除整项商品物料',
				{ type: 'warning', confirmButtonText: '全部删除', cancelButtonText: '取消' },
			).then(() => {
				const req = group.goods_id
					? that.$fetch('addons/hlm/api.FactoryStock/materialDelByGoods', { goods_id: group.goods_id })
					: that.$fetch('addons/hlm/api.FactoryStock/materialDel', { id: ids.join(',') });
				req.then((res) => {
					if (res.code == 1) {
						const n = (res.data && res.data.count) || count;
						that.$message.success(`已删除 ${n} 条物料`);
						that.loadMaterials();
						that.loadMatOptions();
					}
				});
			}).catch(() => {});
		},
		submitInbound() {
			if (!this.inForm.material_id) {
				this.$message.warning('请选择物料');
				return;
			}
			const qty = Number(this.inForm.qty);
			if (!Number.isFinite(qty) || qty === 0) {
				this.$message.warning('请输入非零数量');
				return;
			}
			this.inSaving = true;
			this.$post('addons/hlm/api.FactoryStock/stockIn', {
				material_id: this.inForm.material_id,
				type: this.inForm.type,
				qty,
				remark: this.inForm.remark,
			})
				.then((res) => {
					if (res.code == 1) {
						this.$message.success(res.msg || '成功');
						this.inDialog = false;
						this.inForm.qty = '';
						this.inForm.remark = '';
						this.loadMaterials();
						this.loadMatOptions();
					}
				})
				.finally(() => {
					this.inSaving = false;
				});
		},
		resetDemandSummary() {
			this.demandSummary = {
				order_count: 0,
				amount_total: 0,
				amount_total_text: '0',
				line_total: 0,
				goods_qty_total: 0,
			};
		},
		onDemandRangeChange() {
			this.demandOrders = [];
			this.demandOrdersQueried = false;
			this.demandSelectedIds = [];
			this.demandActiveId = 0;
			this.demandDetailGoods = [];
			this.demandDetailQueried = false;
			this.demandList = [];
			this.demandQueried = false;
			this.demandOrderCount = 0;
			this.resetDemandSummary();
			this.clearDemandOrderSelection();
		},
		goFactoryDetail(id) {
			const oid = Number(id) || 0;
			if (!oid) return;
			this.$router.push({ path: '/factoryDetail', query: { id: String(oid) } });
		},
		clearDemandOrderSelection() {
			const table = this.$refs.demandOrderTable;
			if (table && table.clearSelection) table.clearSelection();
			if (table && table.setCurrentRow) table.setCurrentRow();
		},
		demandRowClassName({ row }) {
			return Number(row.id) === Number(this.demandActiveId) ? 'is-demand-active' : '';
		},
		onDemandOrderSelect(rows) {
			this.demandSelectedIds = (rows || []).map((r) => r.id).filter(Boolean);
		},
		onDemandOrderCurrent(row) {
			if (!row || !row.id) return;
			this.openDemandOrderDetail(row);
		},
		onDemandOrderRowClick(row) {
			const table = this.$refs.demandOrderTable;
			if (table && table.setCurrentRow) table.setCurrentRow(row);
			this.openDemandOrderDetail(row);
		},
		openDemandOrderDetail(row) {
			if (!row || !row.id) return;
			if (Number(this.demandActiveId) === Number(row.id) && this.demandDetailQueried) return;
			this.demandActiveId = row.id;
			this.loadDemandOrderGoods(row.id);
		},
		/** 拉取生产详情同款 goodslist，用于右侧商品统计（非物料换算） */
		loadDemandOrderGoods(orderId) {
			const that = this;
			const id = Number(orderId) || 0;
			if (!id) return;
			that.demandDetailLoading = true;
			that.demandDetailQueried = true;
			that.demandDetailGoods = [];
			that.$fetch('addons/hlm/api.Order/getOderDetails', { id })
				.then((res) => {
					if (res.code == 1 && Number(that.demandActiveId) === id) {
						const detail = res.data || {};
						that.demandDetailGoods = Array.isArray(detail.goodslist) ? detail.goodslist : [];
					}
				})
				.finally(() => {
					that.demandDetailLoading = false;
				});
		},
		loadDemandOrders() {
			const that = this;
			const range = that.demandRangeUnix();
			if (!range) {
				that.$message.warning('请选择时间区间');
				return;
			}
			that.demandOrderLoading = true;
			that.demandList = [];
			that.demandQueried = false;
			that.demandSelectedIds = [];
			that.demandActiveId = 0;
			that.demandDetailGoods = [];
			that.demandDetailQueried = false;
			that.resetDemandSummary();
			that.$post('addons/hlm/api.FactoryStock/unshippedOrderList', range)
				.then((res) => {
					if (res.code == 1) {
						that.demandOrders = (res.data && res.data.orders) || [];
						const summary = (res.data && res.data.summary) || {};
						that.demandSummary = {
							order_count: summary.order_count != null ? summary.order_count : ((res.data && res.data.total) || 0),
							amount_total: Number(summary.amount_total) || 0,
							amount_total_text: summary.amount_total_text != null ? String(summary.amount_total_text) : '0',
							line_total: Number(summary.line_total) || 0,
							goods_qty_total: Number(summary.goods_qty_total) || 0,
						};
						that.demandOrdersQueried = true;
						that.$nextTick(() => {
							that.clearDemandOrderSelection();
							if (that.demandOrders.length) {
								const first = that.demandOrders[0];
								const table = that.$refs.demandOrderTable;
								if (table && table.setCurrentRow) table.setCurrentRow(first);
								that.openDemandOrderDetail(first);
							}
						});
					}
				})
				.finally(() => {
					that.demandOrderLoading = false;
				});
		},
		loadDemand() {
			const that = this;
			const ids = (that.demandSelectedIds || []).slice();
			if (!ids.length) {
				that.$message.warning('请勾选要汇总的订单');
				return;
			}
			that.demandLoading = true;
			that.demandQueried = true;
			that.$post('addons/hlm/api.FactoryStock/unshippedDemand', {
				order_ids: ids.join(','),
			})
				.then((res) => {
					if (res.code == 1) {
						that.demandList = (res.data && res.data.materials) || [];
						that.demandOrderCount = (res.data && res.data.order_count) || 0;
					}
				})
				.finally(() => {
					that.demandLoading = false;
				});
		},
		loadLedger() {
			this.ledgerLoading = true;
			this.$fetch('addons/hlm/api.FactoryStock/ledgerList', {
				page: this.ledgerPage,
				limit: this.ledgerLimit,
				type: this.ledgerType,
			})
				.then((res) => {
					if (res.code == 1) {
						this.ledgerList = (res.data && res.data.data) || [];
						this.ledgerTotal = (res.data && res.data.total) || 0;
					}
				})
				.finally(() => {
					this.ledgerLoading = false;
				});
		},
	},
};
</script>

<style scoped>
.FactoryStockManage {
	--fsm-primary: #0f766e;
	--fsm-border: #e5e7eb;
	--fsm-text: #111827;
	--fsm-muted: #6b7280;
}
.fsm-header { margin-bottom: 12px; }
.fsm-title { margin: 0; font-size: 20px; font-weight: 700; color: var(--fsm-text); }
.fsm-sub { margin: 6px 0 0; font-size: 13px; color: var(--fsm-muted); line-height: 1.5; }
.fsm-tabs { margin-bottom: 8px; }
.fsm-mat-layout {
	display: grid;
	grid-template-columns: 220px minmax(0, 1fr);
	gap: 14px;
	align-items: stretch;
	min-height: 480px;
}
.fsm-mat-layout.is-tree-hidden {
	grid-template-columns: minmax(0, 1fr);
}
.fsm-mat-layout__tree {
	min-width: 0;
	min-height: 0;
	border: 1px solid var(--fsm-border);
	border-radius: 10px;
	background: #fff;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}
.fsm-mat-layout__tree-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	flex-shrink: 0;
	padding: 10px 10px 8px 12px;
	border-bottom: 1px solid var(--fsm-border);
}
.fsm-mat-layout__tree-title {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 13px;
	font-weight: 700;
	line-height: 1.3;
	color: var(--fsm-text);
	cursor: pointer;
	user-select: none;
}
.fsm-mat-layout__tree-title:hover {
	color: var(--fsm-primary);
}
.fsm-mat-layout__tree-hide {
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	margin: 0;
	padding: 0;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: #64748b;
	cursor: pointer;
}
.fsm-mat-layout__tree-hide:hover {
	background: #f3f4f6;
	color: var(--fsm-text);
}
.fsm-mat-layout__tree-toolbar {
	display: flex;
	flex-wrap: nowrap;
	align-items: center;
	justify-content: space-between;
	flex-shrink: 0;
	gap: 2px;
	padding: 6px 8px;
	border-bottom: 1px solid var(--fsm-border);
	background: #fafbfc;
}
.fsm-mat-tb-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 28px;
	margin: 0;
	padding: 0;
	border: 1px solid var(--fsm-border);
	border-radius: 6px;
	background: #fff;
	color: #475569;
	font-size: 14px;
	line-height: 1;
	cursor: pointer;
	transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}
.fsm-mat-tb-btn:hover:not(:disabled) {
	border-color: #94a3b8;
	color: var(--fsm-text);
	background: #fff;
}
.fsm-mat-tb-btn.is-success {
	color: #15803d;
	border-color: #bbf7d0;
	background: #f0fdf4;
}
.fsm-mat-tb-btn.is-primary {
	color: #0f766e;
	border-color: #99f6e4;
	background: #f0fdfa;
}
.fsm-mat-tb-btn.is-danger {
	color: #b91c1c;
	border-color: #fecaca;
	background: #fef2f2;
}
.fsm-mat-tb-btn:disabled {
	opacity: 0.4;
	cursor: not-allowed;
}
.fsm-mat-cat-tree {
	flex: 1;
	min-height: 0;
	overflow: auto;
	padding: 6px;
	display: flex;
	flex-direction: column;
	gap: 2px;
	background: #fff;
}
.fsm-mat-cat-tree__group-label {
	margin: 8px 8px 2px;
	font-size: 11px;
	font-weight: 600;
	color: #94a3b8;
	line-height: 1.4;
}
.fsm-mat-cat-tree__item {
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	min-height: 34px;
	margin: 0;
	padding: 6px 10px;
	border: 0;
	border-radius: 8px;
	background: transparent;
	color: var(--fsm-text);
	font: inherit;
	font-size: 13px;
	line-height: 1.35;
	text-align: left;
	cursor: pointer;
	box-sizing: border-box;
}
.fsm-mat-cat-tree__item:hover {
	background: #f3f4f6;
}
.fsm-mat-cat-tree__item.is-active {
	background: #ecfdf5;
	color: #0f766e;
	font-weight: 700;
}
.fsm-mat-cat-tree__item.is-child {
	padding-left: 16px;
}
.fsm-mat-cat-tree__dot {
	flex-shrink: 0;
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: #cbd5e1;
}
.fsm-mat-cat-tree__item.is-active .fsm-mat-cat-tree__dot {
	background: #14b8a6;
}
.fsm-mat-cat-tree__name {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.fsm-mat-cat-tree__empty {
	padding: 20px 12px;
	font-size: 12px;
	color: var(--fsm-muted);
	text-align: center;
	line-height: 1.6;
}
.fsm-mat-cat-tree__link {
	display: inline;
	border: 0;
	padding: 0;
	margin-left: 4px;
	background: none;
	color: var(--fsm-primary);
	cursor: pointer;
	font: inherit;
}
.fsm-mat-layout__main {
	min-width: 0;
	min-height: 0;
	display: flex;
	flex-direction: column;
}
.fsm-mat-layout__current {
	flex-shrink: 0;
	font-size: 13px;
	font-weight: 600;
	color: var(--fsm-muted);
	padding: 0 2px;
}
@media (max-width: 960px) {
	.fsm-mat-layout {
		grid-template-columns: 1fr;
	}
	.fsm-mat-layout__tree {
		max-height: 280px;
	}
}
.fsm-cat-bar {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 12px;
}
.fsm-cat-bar__main {
	flex: 1;
	min-width: 0;
	display: flex;
	align-items: center;
	gap: 10px;
}
.fsm-cat-bar__label {
	flex-shrink: 0;
	font-size: 13px;
	font-weight: 600;
	color: var(--fsm-muted);
}
.fsm-cat-tabs {
	flex: 1;
	min-width: 0;
}
.fsm-cat-tabs >>> .el-tabs__header { margin-bottom: 0; }
.fsm-cat-tabs >>> .el-tabs__nav-wrap::after { height: 1px; }
.fsm-cat-manage__add {
	display: flex;
	gap: 8px;
	margin-bottom: 12px;
}
.fsm-cat-manage__add .el-input { flex: 1; }
.fsm-card {
	background: #fff;
	border: 1px solid var(--fsm-border);
	border-radius: 10px;
	padding: 16px;
}
.fsm-card--form { max-width: 560px; }
.fsm-toolbar {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 12px;
}
.fsm-toolbar__filters,
.fsm-toolbar__actions {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	align-items: center;
}
.fsm-search { width: 220px; }
.fsm-kind { width: 160px; }
.fsm-pager { display: flex; justify-content: flex-end; margin-top: 12px; }
.fsm-hint { font-size: 12px; color: var(--fsm-muted); line-height: 1.45; margin-top: 4px; }
.fsm-hint--block { margin: 0 0 12px; }
.fsm-demand-meta { font-size: 13px; color: var(--fsm-text); font-weight: 600; }
.fsm-demand-range { width: 280px; }
.fsm-demand-summary {
	display: flex;
	flex-wrap: wrap;
	gap: 8px 16px;
	margin: 0 0 12px;
	padding: 10px 12px;
	border-radius: 8px;
	background: #f8fafc;
	border: 1px solid var(--fsm-border);
	font-size: 13px;
	color: var(--fsm-muted);
}
.fsm-demand-summary strong {
	color: var(--fsm-text);
	font-weight: 700;
}
.fsm-demand-split {
	display: grid;
	grid-template-columns: minmax(420px, 46%) 1fr;
	gap: 14px;
	align-items: start;
	min-height: 420px;
}
.fsm-demand-split__left,
.fsm-demand-split__right {
	min-width: 0;
	border: 1px solid var(--fsm-border);
	border-radius: 10px;
	padding: 12px;
	background: #fff;
}
.fsm-demand-split__left >>> .el-table__body tr {
	cursor: pointer;
}
.fsm-demand-split__left >>> .el-table__body tr.is-demand-active > td {
	background: #ecfdf5 !important;
}
.fsm-demand-stats {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 8px;
	margin-bottom: 14px;
}
.fsm-demand-stat {
	padding: 10px 12px;
	border-radius: 8px;
	background: #f8fafc;
	border: 1px solid #eef2f7;
}
.fsm-demand-stat__label {
	display: block;
	margin-bottom: 4px;
	font-size: 12px;
	color: var(--fsm-muted);
}
.fsm-demand-stat strong {
	font-size: 14px;
	font-weight: 700;
	color: var(--fsm-text);
	word-break: break-all;
}
.fsm-demand-split__title {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	margin-bottom: 10px;
	font-size: 14px;
	font-weight: 700;
	color: var(--fsm-text);
}
.fsm-demand-detail-link {
	font-size: 12px;
	font-weight: 500;
	color: var(--fsm-primary);
	text-decoration: none;
}
.fsm-demand-detail-link:hover { text-decoration: underline; }
.fsm-demand-split__right .print-goods-stats {
	margin-top: 4px;
	padding: 12px 14px 8px;
	border: 1px solid #d1d5db;
	border-radius: 8px;
	background: #fff;
}
.fsm-demand-split__right .print-goods-stats__head {
	display: flex;
	align-items: baseline;
	gap: 10px;
	margin-bottom: 10px;
	padding-bottom: 8px;
	border-bottom: 1px solid #e5e7eb;
}
.fsm-demand-split__right .print-goods-stats__title {
	font-size: 16px;
	font-weight: 700;
	color: #111827;
}
.fsm-demand-split__right .print-goods-stats__sub {
	font-size: 13px;
	font-weight: 600;
	color: #6b7280;
}
.fsm-demand-split__right .print-goods-stats__sub .num {
	color: #111827;
	font-variant-numeric: tabular-nums;
}
.fsm-demand-split__right .print-goods-stats__list {
	display: flex;
	flex-direction: column;
	gap: 6px;
	max-height: 320px;
	overflow: auto;
}
.fsm-demand-split__right .print-goods-stats__row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 6px 0;
	border-bottom: 1px dashed #e5e7eb;
}
.fsm-demand-split__right .print-goods-stats__row:last-child { border-bottom: none; }
.fsm-demand-split__right .print-goods-stats__name {
	flex: 1;
	min-width: 0;
	font-size: 13px;
	font-weight: 600;
	color: #111827;
	line-height: 1.4;
	word-break: break-word;
}
.fsm-demand-split__right .print-goods-stats__fields {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	gap: 14px;
	white-space: nowrap;
}
.fsm-demand-split__right .print-goods-stats__field {
	display: flex;
	align-items: baseline;
	gap: 3px;
	font-size: 13px;
	color: #111827;
}
.fsm-demand-split__right .print-goods-stats__field--length {
	min-width: 120px;
	justify-content: flex-end;
}
.fsm-demand-split__right .print-goods-stats__field--qty {
	min-width: 72px;
	justify-content: flex-end;
}
.fsm-demand-split__right .print-goods-stats__label { color: #6b7280; font-weight: 500; }
.fsm-demand-split__right .print-goods-stats__field .num {
	font-weight: 700;
	font-variant-numeric: tabular-nums;
}
.fsm-demand-split__right .print-goods-stats__unit {
	color: #6b7280;
	font-size: 12px;
}
.fsm-demand-result {
	margin-top: 16px;
	padding-top: 14px;
	border-top: 1px dashed var(--fsm-border);
}
.fsm-demand-result--merge {
	margin-top: 18px;
}
.fsm-demand-result__head {
	display: flex;
	align-items: baseline;
	gap: 10px;
	margin-bottom: 10px;
	font-size: 14px;
}
.fsm-demand-result__head span {
	font-size: 12px;
	color: var(--fsm-muted);
}
@media (max-width: 1100px) {
	.fsm-demand-split {
		grid-template-columns: 1fr;
	}
	.fsm-demand-stats {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}
.fsm-empty { padding: 24px; text-align: center; color: var(--fsm-muted); }
.fsm-list-meta {
	margin: 0 0 12px;
	font-size: 13px;
	color: var(--fsm-muted);
	line-height: 1.5;
}
.fsm-list-meta strong { color: var(--fsm-text); }
.fsm-list-meta__hint {
	margin-left: 8px;
	font-size: 12px;
	color: #94a3b8;
}
.fsm-list-meta__sel {
	margin-left: 10px;
	font-size: 13px;
	color: #0f766e;
}
.fsm-list-meta__sel strong {
	color: #0f766e;
}
.is-neg { color: #dc2626; font-weight: 600; }
.danger { color: #dc2626 !important; }
.fsm-bom-main-title {
	font-size: 15px;
	font-weight: 700;
	color: var(--fsm-text);
}
.fsm-bom-main-meta {
	font-size: 12px;
	color: var(--fsm-muted);
	margin-left: 8px;
}
.fsm-bom-main {
	min-width: 0;
	min-height: 420px;
}
.fsm-toolbar--compact {
	margin-bottom: 8px;
}
.fsm-cat-bar--compact {
	margin-bottom: 10px;
}
.fsm-bom-mat-dlg__target {
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-bottom: 12px;
}
.fsm-bom-mat-dlg__label {
	font-size: 13px;
	font-weight: 600;
	color: var(--fsm-text);
}
.fsm-bom-mat-dlg__product {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 8px;
	padding: 8px 10px;
	border: 1px solid var(--fsm-line, #e5e7eb);
	border-radius: 6px;
	background: #fafafa;
	font-size: 13px;
}
.fsm-bom-tag {
	display: inline-block;
	padding: 1px 6px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	background: #f3f4f6;
}
.fsm-bom-tag.is-process { color: #b45309; background: #fffbeb; }
.fsm-bom-tag.is-ok { color: #0f766e; background: #ecfdf5; }
.fsm-bom-tag.is-pending { color: #b45309; background: #fff7ed; }
.fsm-bom-mat-list {
	overflow: auto;
	padding-right: 2px;
}
.fsm-bom-mat-list--dialog {
	max-height: none;
}
.fsm-bom-mat-table {
	width: 100%;
}
.fsm-bom-mat-table .el-table__row.is-added-row > td {
	background: #f0fdfa !important;
}
.fsm-bom-mat-status {
	font-size: 12px;
	color: #94a3b8;
}
.fsm-bom-mat-status.is-added {
	color: #0f766e;
	font-weight: 600;
}
.fsm-bom-mat__unit { color: #94a3b8; }
.fsm-bom-drop-empty {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 220px;
	padding: 24px;
	text-align: center;
	color: var(--fsm-muted);
	border: 1px dashed var(--fsm-border);
	border-radius: 8px;
	background: #fafafa;
}
.fsm-bom-sku {
	border: 1px solid #cfe3e0;
	border-radius: 8px;
	padding: 6px 8px;
	margin-bottom: 6px;
	background: #eef6f5;
	transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.fsm-bom-sku:last-child { margin-bottom: 0; }
.fsm-bom-sku.is-target {
	background: #e4f3f1;
	border-color: #0f766e;
	box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.12);
}
.fsm-bom-sku__head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	margin-bottom: 2px;
	min-height: 24px;
}
.fsm-bom-sku__title {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px 8px;
	min-width: 0;
	font-size: 13px;
	color: var(--fsm-text);
}
.fsm-bom-sku__title strong { font-size: 13px; }
.fsm-bom-sku__param { color: var(--fsm-muted); }
.fsm-bom-sku__ops {
	display: flex;
	align-items: center;
	gap: 0;
	flex-shrink: 0;
}
.fsm-bom-sku__ops .el-button--text {
	padding: 0 6px;
}
.fsm-bom-list {
	display: flex;
	flex-direction: column;
	gap: 6px;
}
.fsm-bom-sku__summary {
	margin: 0 0 6px;
	font-size: 12px;
	color: var(--fsm-muted);
	line-height: 1.5;
	word-break: break-all;
}
.fsm-bom-sku__empty,
.fsm-bom-inline-empty {
	padding: 0;
	font-size: 12px;
	color: var(--fsm-muted);
	border: none;
	background: transparent;
}
.fsm-bom-sku-table-wrap {
	margin-top: 4px;
	max-height: 220px;
}
.fsm-bom-sku-table {
	min-width: 480px;
}
.fsm-bom-sku-table--compact thead th,
.fsm-bom-sku-table--compact tbody td {
	padding: 2px 6px;
	font-size: 12px;
}
.fsm-bom-sku-table--compact .fsm-spec-chip {
	padding: 0 6px;
	line-height: 20px;
	font-size: 12px;
}
.fsm-bom-sku-table thead th.is-dim {
	min-width: 64px;
}
.fsm-bom-sku-table thead th.is-w,
.fsm-bom-sku-table tbody td.is-w {
	width: 80px;
	min-width: 80px;
}
.fsm-bom-chip-list {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	flex: 1;
	min-width: 0;
}
.fsm-bom-chip {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 0 4px 0 6px;
	border: 1px solid #cfe3e0;
	border-radius: 3px;
	background: #fff;
	font-size: 12px;
	color: var(--fsm-text);
	line-height: 20px;
}
.fsm-bom-chip em {
	font-style: normal;
	color: var(--fsm-muted);
}
.fsm-bom-chip__del {
	border: 0;
	background: transparent;
	color: #9ca3af;
	cursor: pointer;
	padding: 0 2px;
	font-size: 14px;
	line-height: 1;
}
.fsm-bom-chip__del:hover { color: #dc2626; }
.fsm-bom-mat__drive {
	margin-left: 4px;
	font-size: 11px;
	color: var(--fsm-muted);
}
.fsm-bom-editor--compact >>> .el-table__row td {
	padding: 2px 0;
}
.fsm-bom-section--inline {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 6px 8px;
	min-height: 28px;
}
.fsm-bom-section__label {
	flex-shrink: 0;
	width: 36px;
	font-size: 12px;
	font-weight: 700;
	color: var(--fsm-text, #111827);
}
.fsm-bom-section__label em {
	color: #ef4444;
	font-style: normal;
}
.fsm-bom-inline-select {
	min-width: 160px;
	flex: 1;
	max-width: 280px;
}
.fsm-bom-tpl-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
	max-height: 52vh;
	overflow: auto;
}
.fsm-bom-tpl-card {
	display: flex;
	align-items: flex-start;
	gap: 10px;
	margin: 0;
	padding: 12px 14px;
	border: 1px solid var(--fsm-border);
	border-radius: 8px;
	cursor: pointer;
	background: #fff;
	transition: border-color 0.15s, box-shadow 0.15s;
}
.fsm-bom-tpl-card:hover {
	border-color: #94a3b8;
}
.fsm-bom-tpl-card.is-active {
	border-color: #0f766e;
	box-shadow: 0 0 0 1px #0f766e inset;
	background: #f0fdfa;
}
.fsm-bom-tpl-card.is-disabled {
	opacity: 0.55;
	cursor: not-allowed;
}
.fsm-bom-tpl-card input {
	margin-top: 3px;
}
.fsm-bom-tpl-card__title {
	font-size: 14px;
	font-weight: 600;
	color: #0f172a;
}
.fsm-bom-tpl-card__desc {
	margin: 4px 0 0;
	font-size: 12px;
	color: #64748b;
	line-height: 1.45;
}
.fsm-bom-tpl-card__meta {
	margin: 6px 0 0;
	font-size: 12px;
	color: #0f766e;
}

/* 物料列表：统一表头 + 左→右规格列（对齐规格定价） */
.fsm-mat-list {
	min-height: 120px;
}
.fsm-mat-table-wrap {
	border: 1px solid var(--fsm-border);
	border-radius: 10px;
	overflow: auto;
	background: #fff;
}
.fsm-mat-table {
	width: 100%;
	min-width: 960px;
	border-collapse: separate;
	border-spacing: 0;
	font-size: 13px;
	line-height: 1.35;
}
.fsm-mat-table thead th {
	position: sticky;
	top: 0;
	z-index: 2;
	padding: 10px 12px;
	text-align: left;
	font-size: 12px;
	font-weight: 600;
	color: #64748b;
	background: #f8fafc;
	border-bottom: 1px solid var(--fsm-border);
	white-space: nowrap;
}
.fsm-mat-table thead th.is-index,
.fsm-mat-table tbody td.is-index {
	position: sticky;
	left: 0;
	z-index: 3;
	width: 44px;
	min-width: 44px;
	max-width: 44px;
	padding-left: 4px;
	padding-right: 8px;
	text-align: center;
	color: #94a3b8;
	font-variant-numeric: tabular-nums;
	background: #f8fafc;
}
.fsm-mat-table thead th.is-goods {
	position: sticky;
	left: 44px;
	z-index: 3;
	min-width: 140px;
	background: #f1f5f9;
}
.fsm-mat-table thead th.is-dim {
	min-width: 88px;
	color: #0f766e;
	background: #f0fdfa;
}
.fsm-mat-table thead th.is-num,
.fsm-mat-table tbody td.is-num {
	text-align: right;
}
.fsm-mat-table thead th.is-ops,
.fsm-mat-table tbody td.is-ops {
	text-align: right;
	white-space: nowrap;
	padding-right: 14px;
	min-width: 160px;
}
.fsm-mat-table tbody td {
	padding: 10px 12px;
	border-bottom: 1px solid #eef2f7;
	vertical-align: middle;
	background: #fff;
}
.fsm-mat-table__row.is-stripe td {
	background: #fafbfc;
}
.fsm-mat-table__row:hover td {
	background: #f8fafc;
}
.fsm-mat-table tbody td.is-index {
	z-index: 1;
	background: #fff;
}
.fsm-mat-table__row.is-stripe td.is-index {
	background: #fafbfc;
}
.fsm-mat-table__row:hover td.is-index {
	background: #f8fafc;
}
.fsm-mat-table tbody td.is-goods {
	position: sticky;
	left: 44px;
	z-index: 1;
	vertical-align: top;
	border-right: 1px solid #eef2f7;
	background: #fcfcfd;
	min-width: 140px;
	max-width: 200px;
}
.fsm-mat-table__row.is-stripe td.is-goods {
	background: #f8fafc;
}
.fsm-mat-table__row:hover td.is-goods {
	background: #f1f5f9;
}
.fsm-mat-table tbody td.is-goods--clickable {
	cursor: pointer;
}
.fsm-mat-table tbody td.is-goods--clickable:hover {
	background: #eff6ff !important;
}
.fsm-mat-goods-cell__name {
	font-size: 13px;
	font-weight: 700;
	color: var(--fsm-text);
	line-height: 1.35;
	word-break: break-word;
}
.fsm-mat-goods-cell__model {
	margin-top: 2px;
	font-size: 12px;
	color: var(--fsm-muted);
}
.fsm-mat-goods-cell__hint {
	margin-top: 6px;
	font-size: 11px;
	color: #94a3b8;
}
.fsm-mat-goods-cell__cat {
	margin-top: 4px;
	display: inline-block;
	padding: 0 6px;
	border-radius: 4px;
	font-size: 11px;
	color: #0f766e;
	background: #ecfdf5;
	line-height: 1.5;
}
.fsm-mat-goods-cell__ops {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 2px;
	margin-top: 8px;
}
.fsm-mat-link {
	padding: 0;
	border: 0;
	background: transparent;
	color: #0f766e;
	font-size: 12px;
	line-height: 1.5;
	cursor: pointer;
}
.fsm-mat-link:hover {
	color: #0d9488;
	text-decoration: underline;
}
.fsm-mat-link.is-danger {
	color: #dc2626;
}
.fsm-mat-link.is-danger:hover {
	color: #b91c1c;
}
.fsm-mat-table tbody td.is-dim {
	min-width: 88px;
	vertical-align: middle;
}
.fsm-mat-table tbody td.is-dim.is-merged {
	vertical-align: middle;
	background: #f8fffc;
	border-right: 1px solid #e0f2f1;
}
.fsm-mat-table__row.is-stripe td.is-dim.is-merged {
	background: #f3faf7;
}
.fsm-mat-table__row.is-checked td.is-dim.is-merged {
	background: #ecfdf5;
}
.fsm-spec-chip {
	display: inline-block;
	padding: 2px 8px;
	border-radius: 4px;
	background: #f5f7fa;
	border: 1px solid #e4e7ed;
	color: #303133;
	font-size: 12px;
	font-weight: 600;
	line-height: 1.4;
	max-width: 140px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	vertical-align: middle;
}
.fsm-spec-empty {
	color: #cbd5e1;
}
.fsm-mat-empty-row {
	color: var(--fsm-muted);
	font-size: 12px;
}
.fsm-mat-table tbody td.is-empty-span {
	color: var(--fsm-muted);
}
.fsm-stock-class-tag {
	display: inline-block;
	padding: 1px 7px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 600;
	line-height: 1.4;
}
.fsm-stock-class-tag.is-finished {
	color: #1d4ed8;
	background: #eff6ff;
}
.fsm-stock-class-tag.is-raw {
	color: #0f766e;
	background: #ecfdf5;
}
.fsm-mat-table tbody td.is-meta {
	color: #4b5563;
	white-space: nowrap;
}
.fsm-mat-table tbody td.is-num {
	font-variant-numeric: tabular-nums;
	font-weight: 600;
}
.fsm-mat-table tbody td.is-ops .el-button {
	padding: 0 4px;
	margin-left: 2px;
}
.fsm-mat-goods-locked {
	min-height: 32px;
	padding: 6px 10px;
	border-radius: 4px;
	background: #f5f7fa;
	color: var(--fsm-text);
	font-size: 13px;
	line-height: 1.4;
	border: 1px solid var(--fsm-border);
}
.fsm-class-tag {
	display: inline-block;
	padding: 1px 6px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 600;
	line-height: 1.4;
}
.fsm-class-tag.is-finished {
	color: #0f766e;
	background: #ecfdf5;
}
.fsm-class-tag.is-raw {
	color: #b45309;
	background: #fffbeb;
}

.fsm-pick-sku__tag {
	margin-left: 6px;
	font-size: 11px;
	color: #0f766e;
	background: #ecfdf5;
	border-radius: 4px;
	padding: 0 6px;
	line-height: 18px;
}
.fsm-pick-sku__tag.is-configured { color: #0f766e; background: #ecfdf5; }
.fsm-pick-sku__tag.is-pending { color: #b45309; background: #fff7ed; }
.fsm-pick__footer-meta {
	float: left;
	line-height: 32px;
	font-size: 13px;
	color: var(--fsm-muted);
}
@media (max-width: 1200px) {
	.fsm-mat-table {
		min-width: 760px;
	}
}
.fsm-bom-cart-foot {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-top: 16px;
	padding-top: 12px;
	border-top: 1px solid #e8ecef;
}
.fsm-bom-cart-foot .fsm-hint {
	margin: 0;
	flex: 1;
}
.fsm-bom-loss {
	color: #b45309;
	font-weight: 600;
}
.fsm-bom-section {
	margin-top: 4px;
	padding-top: 4px;
	border-top: 1px dashed var(--fsm-line, #e5e7eb);
}
.fsm-bom-section:first-of-type {
	border-top: none;
	padding-top: 0;
	margin-top: 0;
}
.fsm-bom-section__title {
	font-size: 12px;
	font-weight: 700;
	color: var(--fsm-text, #111827);
	margin-bottom: 0;
}
.fsm-bom-section__title--row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	min-height: 24px;
}
.fsm-bom-section__title em {
	color: #ef4444;
	font-style: normal;
	margin-left: 2px;
}
.fsm-bom-section__hint {
	display: none;
}
.fsm-bom-strip-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 6px 8px;
	margin-top: 0;
}
.fsm-bom-strip-meta {
	font-size: 12px;
	color: var(--fsm-muted, #6b7280);
	white-space: nowrap;
}
.fsm-bom-strip-loss {
	font-size: 12px;
	color: var(--fsm-muted, #6b7280);
	white-space: nowrap;
}
.fsm-muted {
	color: #9ca3af;
}
.fsm-bom-param-note {
	margin-top: 14px;
	padding: 10px 12px;
	border: 1px solid #e8ecef;
	border-radius: 8px;
	background: #fafafa;
}
.fsm-bom-param-note__title {
	font-size: 13px;
	font-weight: 700;
	margin-bottom: 4px;
}
.fsm-bom-param-note .fsm-hint {
	margin: 0;
}
.fsm-mat-spec-editor {
	min-height: 200px;
}
.fsm-mat-spec-dims {
	margin-bottom: 16px;
}
.fsm-mat-spec-dims__title {
	font-size: 13px;
	font-weight: 700;
	margin-bottom: 8px;
	color: var(--fsm-text, #111827);
}
.fsm-mat-spec-empty {
	padding: 16px;
	text-align: center;
	color: #94a3b8;
	font-size: 13px;
	background: #f8fafc;
	border-radius: 8px;
}
.fsm-mat-spec-dims__list {
	min-height: 4px;
}
.fsm-mat-spec-dim {
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 10px 12px;
	margin-bottom: 10px;
	background: #fff;
}
.fsm-mat-spec-dim--ghost {
	opacity: 0.45;
}
.fsm-mat-spec-dim--chosen {
	border-color: #93c5fd;
	box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}
.fsm-mat-spec-dim__main,
.fsm-mat-spec-dim__values {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
}
.fsm-mat-spec-dim__values {
	margin-top: 8px;
	padding-top: 8px;
	border-top: 1px dashed #e5e7eb;
}
.fsm-mat-spec-dim__label {
	font-size: 12px;
	color: #64748b;
	min-width: 52px;
}
.fsm-mat-spec-drag,
.fsm-mat-spec-value-drag {
	color: #94a3b8;
	cursor: grab;
	font-size: 14px;
	padding: 2px;
	user-select: none;
}
.fsm-mat-spec-drag:hover,
.fsm-mat-spec-value-drag:hover {
	color: #64748b;
}
.fsm-mat-spec-drag:active,
.fsm-mat-spec-value-drag:active {
	cursor: grabbing;
}
.fsm-mat-spec-values-list {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 6px;
}
.fsm-mat-spec-value {
	display: inline-flex;
	align-items: center;
	gap: 2px;
}
.fsm-mat-spec-value--ghost {
	opacity: 0.45;
}
.fsm-mat-spec-value--chosen .fsm-mat-spec-tag {
	box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
.fsm-mat-spec-tag {
	cursor: default;
}
.fsm-mat-spec-tag span {
	cursor: pointer;
}
.fsm-mat-spec-combos {
	margin-top: 8px;
}
.fsm-mat-spec-combo-wrap {
	max-height: 420px;
	overflow: auto;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
}
.fsm-mat-spec-combo-table {
	min-width: 880px;
}
.fsm-mat-spec-combo-table thead th.is-index,
.fsm-mat-spec-combo-table tbody td.is-index {
	position: static;
	left: auto;
	background: inherit;
}
.fsm-mat-spec-combo-table thead th.is-dim {
	position: sticky;
	top: 0;
	z-index: 2;
}
.fsm-mat-spec-combo-table tbody td.is-dim.is-merged {
	vertical-align: middle;
	background: #f8fafc;
}
.fsm-mat-spec-combo-table .el-input,
.fsm-mat-spec-combo-table .el-select {
	width: 100%;
}
.fsm-mat-spec-footer {
	display: flex;
	align-items: center;
	width: 100%;
}
.fsm-mat-spec-footer__spacer {
	flex: 1;
}
</style>

<style>
@import '../../assets/css/good-dialog.css';
@import '../../assets/css/cart-dialog.css';
.fsm-bom-cart-dialog .cart_good {
	width: 100% !important;
	max-width: 100%;
}
.good_dialog .search_img .el-icon-search {
	font-size: 16px;
	color: #9ca3af;
}
</style>
